import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as THREE from 'three';
import {
  forceSimulation, forceLink, forceManyBody,
  forceZ, forceX, forceY
} from 'd3-force-3d';
import { EffectComposer, RenderPass, BloomEffect, EffectPass } from 'postprocessing';
import './RealityLayersViz.css';

const LAYER_BUTTONS = [
  { key: 'all', label: 'All' },
  { key: 'physical', label: 'Physical' },
  { key: 'personal_physical', label: 'Body' },
  { key: 'family', label: 'Family' },
  { key: 'spirituality', label: 'Spirit' },
  { key: 'social', label: 'Social' },
  { key: 'work', label: 'Work' },
  { key: 'civic_government', label: 'Civic' },
  { key: 'entertainment', label: 'Interests' },
];

const BTN_BASE = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: 'rgba(255,255,255,0.35)',
  padding: '5px 12px',
  fontFamily: 'inherit',
  fontSize: 10,
  fontWeight: 500,
  cursor: 'pointer',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  borderRadius: 3,
};

const BTN_ACTIVE = {
  ...BTN_BASE,
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.32)',
  color: 'rgba(255,255,255,0.9)',
};

const RealityLayersViz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const selPanelRef = useRef(null);
  const selTitleRef = useRef(null);
  const selLayerRef = useRef(null);
  const selNoteRef = useRef(null);
  const selAxesRef = useRef(null);
  const selCostsRef = useRef(null);
  const selCountRef = useRef(null);
  const selConnectionsRef = useRef(null);
  const selScoreRef = useRef(null);
  const layerLabelRef = useRef(null);
  const layerDescRef = useRef(null);
  const loadingRef = useRef(null);
  const layerBtnsRef = useRef({});
  const layerClickRef = useRef(null);
  const clearSelRef = useRef(null);

  const isMobileLayout = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

  // ─── Quiz persistence ────────────────────────────────────────────
  const QUIZ_KEY = 'rl-quiz-overrides';
  const _persisted = (() => {
    try {
      const raw = typeof window !== 'undefined' && window.localStorage.getItem(QUIZ_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const quizOverridesRef = useRef(_persisted?.overrides ?? {});
  const applyOverlayRef = useRef(null);
  const allNodesRef = useRef([]);   // populated after init — used by profile panel
  const layersMetaRef = useRef({});   // LAYERS map for layer labels in profile panel

  // ─── Quiz state ─────────────────────────────────────────────────
  const [quizOpen, setQuizOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [domainIdx, setDomainIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [showFollowup, setShowFollowup] = useState(false);
  // Restore mode from localStorage so refresh keeps the personal view active
  const [overlayMode, setOverlayMode] = useState(_persisted ? 'personal' : null);

  // ─── Shock propagation state ─────────────────────────────────────────────
  const shockResultRef      = useRef(null);
  const triggerShockRef     = useRef(null);
  const shockModeRef        = useRef(false);
  const shockDeltaRef       = useRef({ attention: 0.3, energy: 0.2, money: 0.0 });
  const [shockPanelVisible, setShockPanelVisible] = useState(false);
  const [shockResult,       setShockResult]        = useState(null);
  const [shockMode,         setShockMode]          = useState(false);
  const [shockDelta,        setShockDelta]         = useState({ attention: 0.3, energy: 0.2, money: 0.0 });
  const [shockType,         setShockType]          = useState('additive');
  const [selectedShockNode, setSelectedShockNode]  = useState(null);

  // ─── Mobile shock state ────────────────────────────────────────────────────
  const [mobileShockView,  setMobileShockView]   = useState(false);
  const [mobileMagnitude,  setMobileMagnitude]   = useState(0.5);
  const [mobileDeltaBase,  setMobileDeltaBase]   = useState({ attention: 1.0, energy: 0.7, money: 0.3 });
  const mobileShockNodeRef  = useRef(null);
  const mobileShockDeltaRef = useRef({ attention: 0.5, energy: 0.35, money: 0.15 });

  // ─── Mobile bottom sheet drag-to-snap ─────────────────────────────────────
  // Three snap positions: peek (just header, see graph) / normal / full (all rows)
  const [sheetSnap,    setSheetSnap]    = useState('normal');
  const sheetHandleRef = useRef(null);

  useEffect(() => { shockModeRef.current  = shockMode;  }, [shockMode]);
  useEffect(() => { shockDeltaRef.current = shockDelta; }, [shockDelta]);
  useEffect(() => {
    mobileShockDeltaRef.current = {
      attention: mobileMagnitude * mobileDeltaBase.attention,
      energy:    mobileMagnitude * mobileDeltaBase.energy,
      money:     mobileMagnitude * mobileDeltaBase.money,
    };
  }, [mobileMagnitude, mobileDeltaBase]);

  // Reset sheet to normal snap whenever the shock view is entered/exited
  useEffect(() => {
    if (isMobileLayout) setSheetSnap('normal');
  }, [mobileShockView]); // eslint-disable-line

  // Wire drag-to-snap on the pill handle (non-passive touchmove so preventDefault works)
  useEffect(() => {
    if (!isMobileLayout) return;
    const handle = sheetHandleRef.current;
    const panel  = selPanelRef.current;
    if (!handle || !panel) return;

    let startY = 0;
    let startH = 0;

    const onStart = (e) => {
      startY = e.touches[0].clientY;
      startH = panel.offsetHeight;
      panel.style.transition = 'none';
    };

    const onMove = (e) => {
      e.preventDefault();
      const dy  = startY - e.touches[0].clientY; // positive = dragging up
      const newH = Math.min(window.innerHeight * 0.88, Math.max(60, startH + dy));
      panel.style.maxHeight = newH + 'px';
    };

    const onEnd = () => {
      const h  = panel.offsetHeight;
      const vh = window.innerHeight;
      panel.style.transition = 'max-height 0.25s ease';
      if      (h < vh * 0.33) setSheetSnap('peek');
      else if (h < vh * 0.66) setSheetSnap('normal');
      else                    setSheetSnap('full');
    };

    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove',  onMove,  { passive: false });
    handle.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      handle.removeEventListener('touchstart', onStart);
      handle.removeEventListener('touchmove',  onMove);
      handle.removeEventListener('touchend',   onEnd);
    };
  }, []); // runs once after mount; refs are stable

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const W = () => container.clientWidth;
    const H = () => container.clientHeight;

    // ─── Constants ──────────────────────────────────────────────────
    const AXIS_SCALE = 550;

    const LINK_STYLE = {
      support: { opacity: 0.35, dimOpacity: 0.22 },
      amplify: { opacity: 0.50, dimOpacity: 0.32 },
      drain: { opacity: 0.45, dimOpacity: 0.28 },
      conflict: { opacity: 0.40, dimOpacity: 0.25 },
      neutral: { opacity: 0.20, dimOpacity: 0.12 },
    };

    const LINK_COLOR = {
      support: 0xaaffaa,
      amplify: 0xaaaaff,
      drain: 0xff8888,
      conflict: 0xff4444,
      neutral: 0xffffff,
    };

    const NODE_TYPE_SCALE = {
      resource: 1.1, constraint: 1.2, practice: 1.0,
      system: 1.3, relationship: 1.0, state: 0.9,
    };

    // ─── Outer-scope refs for cleanup ───────────────────────────────
    let renderer = null;
    let composer = null;
    let ro = null;
    let onKeyDown = null;
    let onKeyUp = null;
    let animId = null;

    // ─── Scene ──────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x030508);
    scene.fog = new THREE.FogExp2(0x030508, 0.0005);

    const camera = new THREE.PerspectiveCamera(55, W() / H(), 0.1, 8000);
    camera.position.set(0, 0, 1200);

    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W(), H());
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(new EffectPass(camera, new BloomEffect({
      intensity: 1.8, luminanceThreshold: 0.05, luminanceSmoothing: 0.4, radius: 0.7,
    })));

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const pointLight = new THREE.PointLight(0xffffff, 0.6, 3000);
    pointLight.position.set(0, 500, 1000);
    scene.add(pointLight);

    // ─── Async init ─────────────────────────────────────────────────
    const init = async () => {
      let data;
      try {
        data = await fetch('/data/reality-layers.json').then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        });
      } catch (err) {
        console.error('Failed to load reality-layers.json:', err);
        if (loadingRef.current) loadingRef.current.textContent = 'Failed to load data — check console';
        return;
      }

      // ─── Flatten data ───────────────────────────────────────────────
      const LAYERS = data.layers;
      const allNodes = [];
      const allLinks = [];
      const nodeMap = {};

      Object.entries(LAYERS).forEach(([layerKey, layer]) => {
        layer._color = new THREE.Color(layer.color);
        layer.nodes.forEach(node => {
          const n = {
            ...node,
            layer: layerKey,
            layerColor: layer._color,
            layerZ: layer.zOffset,
            targetX: (node.pos?.x ?? 0) * AXIS_SCALE,
            targetY: (node.pos?.y ?? 0) * AXIS_SCALE,
          };
          allNodes.push(n);
          nodeMap[node.id] = n;
        });
        layer.links.forEach(link => allLinks.push({ ...link, crossLayer: false }));
      });
      data.crossLinks.forEach(link => allLinks.push({ ...link, crossLayer: true }));

      // Expose to React — profile panel reads these without touching Three.js
      allNodesRef.current = allNodes;
      layersMetaRef.current = LAYERS;

      // ─── Overlay engine ─────────────────────────────────────────────
      // Reads data.overlay, stamps effective_cost onto every node, and
      // marks nodes inactive if overridden. When overlay.active is false
      // (or overlay is absent), effective_cost === base cost for all nodes.
      //
      // effective_cost(n)[res] = cost[res]  (explicit override, wins)
      //                       ?? base_cost[res] × weight_multiplier
      //
      // This is the character sheet merge: universal scaffold + personal diff.

      const OVERLAY = data.overlay || { active: false, overrides: {} };
      let OVERLAY_ON = OVERLAY.active === true;
      let OV_OVERRIDES = OVERLAY.overrides || {};

      allNodes.forEach(node => {
        const ov = OV_OVERRIDES[node.id];

        // Active flag — inactive nodes are excluded from personal budget
        node.overlay_active = !ov || ov.active !== false;

        if (!OVERLAY_ON || !ov) {
          // Baseline: effective cost == base cost
          node.effective_cost = { ...node.cost };
          node.overlay_source = null;
        } else {
          const mult = ov.weight_multiplier ?? 1.0;
          const costOv = ov.cost || {};
          node.effective_cost = {
            attention: costOv.attention ?? (node.cost?.attention || 0) * mult,
            energy: costOv.energy ?? (node.cost?.energy || 0) * mult,
            money: costOv.money ?? (node.cost?.money || 0) * mult,
          };
          node.overlay_source = ov.source || 'manual';
          node.overlay_note = ov.note || null;
        }
      });

      // Active budget: sum of effective costs across active nodes only
      // Used by personal load ratio. In baseline mode this equals graph total.
      const ACTIVE_BUDGET = { attention: 0, energy: 0, money: 0 };
      allNodes.forEach(n => {
        if (!n.overlay_active) return;
        const totalCost = (n.effective_cost?.attention || 0) + (n.effective_cost?.energy || 0) + (n.effective_cost?.money || 0);
        if (totalCost < 0.1) return;  // below conscious threshold — runs silently
        ACTIVE_BUDGET.attention += n.effective_cost?.attention || 0;
        ACTIVE_BUDGET.energy += n.effective_cost?.energy || 0;
        ACTIVE_BUDGET.money += n.effective_cost?.money || 0;
      });

      // ─── Force Simulation ───────────────────────────────────────────
      const simulation = forceSimulation(allNodes, 3)
        .force('link', forceLink(allLinks).id(d => d.id).distance(110).strength(d => (d.strength || 0.5) * 0.6))
        .force('charge', forceManyBody().strength(-200))
        .force('z', forceZ(d => d.layerZ).strength(0.6))
        .force('posX', forceX(d => d.targetX).strength(0.25))
        .force('posY', forceY(d => d.targetY).strength(0.25))
        .stop();

      for (let i = 0; i < 400; i++) simulation.tick();

      // ─── Sprite helper ───────────────────────────────────────────────
      function makeSprite(text, color, opts = {}) {
        const { fontSize = 22, bold = true, bgAlpha = 0.55, w = 512, h = 80 } = opts;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        const hex = color instanceof THREE.Color ? `#${color.getHexString()}` : color;
        ctx.font = `${bold ? 'bold' : '300'} ${fontSize}px system-ui, -apple-system, "Segoe UI", sans-serif`;
        const tw = ctx.measureText(text).width;
        const pad = 16, pw = tw + pad * 2, ph = 36;
        const px = (w - pw) / 2, py = (h - ph) / 2;
        if (bgAlpha > 0) {
          ctx.fillStyle = `rgba(0,0,0,${bgAlpha})`;
          ctx.beginPath(); ctx.roundRect(px, py, pw, ph, 6); ctx.fill();
        }
        ctx.fillStyle = hex;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(text, w / 2, h / 2);
        const mat = new THREE.SpriteMaterial({
          map: new THREE.CanvasTexture(canvas), transparent: true, depthWrite: false,
        });
        return new THREE.Sprite(mat);
      }

      // ─── Nodes ──────────────────────────────────────────────────────
      const nodeObjects = [];

      allNodes.forEach(node => {
        const size = (node.weight || 1) * 4.5 * (NODE_TYPE_SCALE[node.type] || 1);
        const mat = new THREE.MeshStandardMaterial({
          color: node.layerColor, emissive: node.layerColor, emissiveIntensity: 0.35,
          transparent: true, opacity: 0.9, roughness: 0.4, metalness: 0.2,
        });
        const mesh = new THREE.Mesh(new THREE.SphereGeometry(size, 24, 24), mat);
        mesh.position.set(node.x || 0, node.y || 0, node.z || 0);
        mesh.userData = { node, size, baseScale: 1.0 };
        scene.add(mesh);
        nodeObjects.push(mesh);

        if (node.weight >= 3 || node.type === 'constraint') {
          const baseGlowOpacity = node.type === 'constraint' ? 0.08 : 0.04;
          const glowMat = new THREE.MeshBasicMaterial({
            color: node.layerColor, transparent: true,
            opacity: baseGlowOpacity,
            side: THREE.BackSide,
          });
          const glow = new THREE.Mesh(new THREE.SphereGeometry(size * 2.4, 16, 16), glowMat);
          glow.position.copy(mesh.position);
          scene.add(glow);
          mesh.userData.glowMesh = glow;
          mesh.userData.baseGlowOpacity = baseGlowOpacity;
        }
      });

      // ─── Links ──────────────────────────────────────────────────────
      const linkObjects = [];

      allLinks.forEach(link => {
        const src = typeof link.source === 'object' ? link.source : nodeMap[link.source];
        const tgt = typeof link.target === 'object' ? link.target : nodeMap[link.target];
        if (!src || !tgt) return;

        const style = LINK_STYLE[link.type] || LINK_STYLE.neutral;
        const isCross = link.crossLayer;
        const color = isCross ? (LINK_COLOR[link.type] || 0xffffff) : src.layerColor;
        const points = [
          new THREE.Vector3(src.x || 0, src.y || 0, src.z || 0),
          new THREE.Vector3(tgt.x || 0, tgt.y || 0, tgt.z || 0),
        ];
        const mat = new THREE.LineBasicMaterial({
          color, transparent: true,
          opacity: isCross ? style.opacity : style.dimOpacity,
        });
        const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), mat);
        scene.add(line);

        let cone = null;
        if (link.directed) {
          const srcVec = points[0];
          const tgtVec = points[1];
          const dir = new THREE.Vector3().subVectors(tgtVec, srcVec).normalize();
          const pos = new THREE.Vector3().lerpVectors(srcVec, tgtVec, 0.62);
          const coneGeo = new THREE.ConeGeometry(5, 20, 8);
          const coneMat = new THREE.MeshBasicMaterial({
            color, transparent: true,
            opacity: isCross ? style.opacity : style.dimOpacity,
          });
          cone = new THREE.Mesh(coneGeo, coneMat);
          cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
          cone.position.copy(pos);
          scene.add(cone);
        }

        linkObjects.push({ line, link, sourceNode: src, targetNode: tgt, style, cone });
      });

      // ─── Adjacency index ────────────────────────────────────────────
      const nodeConnections = {};
      allNodes.forEach(n => { nodeConnections[n.id] = []; });
      linkObjects.forEach(({ line, link, sourceNode, targetNode }) => {
        nodeConnections[sourceNode.id].push({ line, link, otherNode: targetNode });
        nodeConnections[targetNode.id].push({ line, link, otherNode: sourceNode });
      });

      // ─── Phase 2: Cost Propagation Engine ───────────────────────────
      //
      // Formalizes attention/energy/money as finite resource flows.
      // Each node has a base cost vector. Neighbors propagate costs
      // through typed edges using drain coefficients from the model spec.
      //
      // Δcost(target) = Δcost(source) × edge_strength × drain_coefficient(type)
      // Propagation attenuates at depth-2: × 0.4
      //
      // Layer hierarchy weights (higher = more authoritative):
      // physical(1.0) > personal_physical(0.9) > family(0.8) > spirituality(0.75)
      //   > social(0.7) > work(0.6) > civic(0.5) > entertainment(0.4)

      const LAYER_WEIGHT = {
        physical: 1.0, personal_physical: 0.9, family: 0.8,
        spirituality: 0.75, social: 0.7, work: 0.6,
        civic_government: 0.5, entertainment: 0.4,
      };

      // Drain coefficients per edge type
      // Positive = adds cost to target, negative = reduces cost (stabilizes)
      const DRAIN_COEFF = {
        drain: 0.9,    // direct cost transfer
        amplify: 0.7,  // cost amplification
        conflict: -0.3, // active opposition / relief
        support: -0.15, // stabilization
        neutral: 0.05, // background informational
      };

      // Shock Propagation
      const ATTENUATION_2ND = 0.4; 
      function propagateShock(originId, delta, nodes, connIndex, drainCoeff, layerWeight, attenuation = 0.4, maxDepth = 3) {
        // nodeMap for O(1) lookup
        const nodeMap = {};
        nodes.forEach(n => { nodeMap[n.id] = n; });

        const origin = nodeMap[originId];
        if (!origin) return [];

        // BFS queue entries: { nodeId, currentDelta, depth, path }
        const queue = [{ nodeId: originId, currentDelta: { ...delta }, depth: 0, path: [] }];
        const visited = new Set([originId]);

        // Accumulate deltas — a node may be reached via multiple paths
        // We sum all contributions (this is physically correct: multiple pathways
        // into a node all apply their cost simultaneously)
        const accumulator = {}; // nodeId → { deltaAttention, deltaEnergy, deltaMoney, depth, paths[] }

        while (queue.length > 0) {
          const { nodeId, currentDelta, depth, path } = queue.shift();

          // Record accumulation for this node (skip origin — it's the source)
          if (nodeId !== originId) {
            if (!accumulator[nodeId]) {
              accumulator[nodeId] = {
                nodeId,
                label: nodeMap[nodeId]?.label || nodeId,
                layer: nodeMap[nodeId]?.layer || 'unknown',
                depth,
                deltaAttention: 0,
                deltaEnergy: 0,
                deltaMoney: 0,
                edgePath: path,
              };
            }
            accumulator[nodeId].deltaAttention += currentDelta.attention;
            accumulator[nodeId].deltaEnergy += currentDelta.energy;
            accumulator[nodeId].deltaMoney += currentDelta.money;
            // Keep the shallowest depth seen (first BFS arrival = shortest path)
            if (depth < accumulator[nodeId].depth) {
              accumulator[nodeId].depth = depth;
              accumulator[nodeId].edgePath = path;
            }
          }

          if (depth >= maxDepth) continue;

          const connections = connIndex[nodeId] || [];
          const sourceLayerW = layerWeight[nodeMap[nodeId]?.layer] ?? 0.5;

          connections.forEach(({ link, otherNode }) => {
            if (!otherNode || !otherNode.id) return;

            // Respect directionality: shocks only flow source→target on directed edges
            // On undirected edges they flow both ways
            const targetId = typeof link.target === 'object' ? link.target?.id : link.target;
            const isDirected = !!link.directed;
            if (isDirected && targetId !== otherNode.id) {
              // This connection is otherNode→nodeId, so nodeId is the target.
              // Shocks don't travel upstream on directed edges.
              return;
            }

            const targetLayerW = layerWeight[otherNode.layer] ?? 0.5;
            const kappa = drainCoeff[link.type] ?? 0.05;
            const strength = link.strength ?? 0.5;

            // Layer asymmetry: higher layers push down more forcefully than upward
            // If source layer weight > target layer weight → full coefficient
            // If source layer weight < target layer weight → reduced by ratio
            const layerAsymmetry = sourceLayerW >= targetLayerW
              ? 1.0
              : sourceLayerW / targetLayerW; // e.g. work→physical is 0.6/1.0 = 0.6× weakened

            // Δcost(target) = Δcost(source) × κ(edge_type) × strength × layerAsymmetry × attenuation^depth
            const hopAttenuation = Math.pow(attenuation, depth); // depth of arrival (0 = from origin)
            const factor = kappa * strength * layerAsymmetry * hopAttenuation;

            const propagatedDelta = {
              attention: currentDelta.attention * factor,
              energy: currentDelta.energy * factor,
              money: currentDelta.money * factor,
            };

            // Threshold — below 0.002 per resource the signal is below perceptual noise
            const totalMag = Math.abs(propagatedDelta.attention) + Math.abs(propagatedDelta.energy) + Math.abs(propagatedDelta.money);
            if (totalMag < 0.002) return;

            // Allow re-visiting via shorter/stronger path (don't use visited for accumulation)
            // But cap total visits to prevent exponential blowup on dense graphs
            const visitKey = `${otherNode.id}@${depth + 1}`;
            if (visited.has(visitKey)) return;
            visited.add(visitKey);

            queue.push({
              nodeId: otherNode.id,
              currentDelta: propagatedDelta,
              depth: depth + 1,
              path: [...path, link.type],
            });
          });
        }

        // Convert accumulator to sorted array
        const result = Object.values(accumulator).map(entry => ({
          ...entry,
          totalAbsDelta: Math.abs(entry.deltaAttention) + Math.abs(entry.deltaEnergy) + Math.abs(entry.deltaMoney),
        }));

        result.sort((a, b) => b.totalAbsDelta - a.totalAbsDelta);
        return result;
      }

      // ─── ShockAnimator — Three.js cascade animation ───────────────────────────
      class ShockAnimator {
        constructor(nodeObjects, linkObjects, allNodes, scene, THREE) {
          this.nodeObjects = nodeObjects;
          this.linkObjects = linkObjects;
          this.allNodes    = allNodes;
          this.scene       = scene;
          this.THREE       = THREE;
          this.meshByNodeId = {};
          nodeObjects.forEach((mesh, i) => {
            this.meshByNodeId[allNodes[i].id] = { mesh, index: i };
          });
          this.active = false;
          this.waves  = [];
          this.startTime = null;
          this.totalDuration = 0;
          this.onComplete = null;
        }

        start(originId, originDelta, cascadeDiff, onComplete) {
          this.stop();
          this.onComplete = onComplete;
          this.active     = true;
          this.startTime  = performance.now();

          const HOP_INTERVAL = 400;
          const WAVE_DURATION = 700;
          const waveMap = {};

          const originMag  = Math.abs(originDelta.attention) + Math.abs(originDelta.energy) + Math.abs(originDelta.money);
          const originSign = originDelta.attention + originDelta.energy + originDelta.money >= 0 ? 1 : -1;
          waveMap[0] = [{ nodeId: originId, magnitude: Math.min(1, originMag * 1.5), sign: originSign, depth: 0 }];

          cascadeDiff.forEach(entry => {
            const d = entry.depth;
            if (!waveMap[d]) waveMap[d] = [];
            const mag  = Math.min(1, entry.totalAbsDelta * 3);
            const sign = entry.deltaAttention + entry.deltaEnergy + entry.deltaMoney >= 0 ? 1 : -1;
            waveMap[d].push({ nodeId: entry.nodeId, magnitude: mag, sign, depth: d });
          });

          this.waves = Object.entries(waveMap)
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([depth, items]) => ({
              startT:   Number(depth) * HOP_INTERVAL,
              duration: WAVE_DURATION,
              depth:    Number(depth),
              items,
            }));

          const lastWave = this.waves[this.waves.length - 1];
          this.totalDuration = lastWave ? lastWave.startT + lastWave.duration + 200 : 1500;
        }

        stop() {
          this.active = false;
          this.waves  = [];
        }

        tick(now, getBaseMeshState) {
          if (!this.active) return false;
          const elapsed = now - this.startTime;
          if (elapsed >= this.totalDuration) {
            this.active = false;
            if (this.onComplete) this.onComplete();
            return false;
          }

          this.waves.forEach(wave => {
            const localT = elapsed - wave.startT;
            if (localT < 0 || localT > wave.duration) return;
            const phase = localT / wave.duration;
            const envelope = phase < 0.3 ? phase / 0.3 : phase < 0.7 ? 1.0 : 1.0 - (phase - 0.7) / 0.3;

            wave.items.forEach(({ nodeId, magnitude, sign, depth }) => {
              const entry = this.meshByNodeId[nodeId];
              if (!entry) return;
              const { mesh } = entry;

              let pulseColor;
              if (depth === 0) {
                pulseColor = new this.THREE.Color(0xffffff);
              } else if (sign > 0) {
                pulseColor = new this.THREE.Color().lerpColors(
                  new this.THREE.Color(0xff8800), new this.THREE.Color(0xff2200), magnitude
                );
              } else {
                pulseColor = new this.THREE.Color(0x22ff88);
              }

              const intensity  = magnitude * envelope;
              const baseState  = getBaseMeshState(nodeId);
              mesh.material.color.lerpColors(baseState.color, pulseColor, intensity * 0.85);
              mesh.material.emissive.lerpColors(baseState.emissive, pulseColor, intensity);
              mesh.material.emissiveIntensity = baseState.emissiveIntensity + intensity * 1.8;
              mesh.material.opacity = Math.min(1.0, baseState.opacity + intensity * 0.4);

              const scaleMult = depth === 0 ? 1 + envelope * 0.55 : 1 + envelope * 0.25 * magnitude;
              const baseScale = mesh.userData.baseScale ?? 1.0;
              mesh.scale.setScalar(baseScale * scaleMult);

              if (mesh.userData.glowMesh) {
                const glow = mesh.userData.glowMesh;
                glow.material.color.copy(pulseColor);
                glow.material.opacity = (mesh.userData.baseGlowOpacity ?? 0.04) + intensity * 0.25;
              }
            });
          });

          return true;
        }
      }

      const shockAnimator = new ShockAnimator(nodeObjects, linkObjects, allNodes, scene, THREE);

      function getMeshBaseState(nodeId) {
        const entry = shockAnimator.meshByNodeId[nodeId];
        if (!entry) return { color: new THREE.Color(0xffffff), emissive: new THREE.Color(0), emissiveIntensity: 0.35, opacity: 0.9 };
        const { mesh, index } = entry;
        const node = allNodes[index];
        return {
          color:             node.layerColor.clone(),
          emissive:          node.layerColor.clone(),
          emissiveIntensity: selectedNodeId !== null ? (node.id === selectedNodeId ? 0.8 : 0.0) : 0.35,
          opacity:           mesh.material.opacity,
        };
      }

      function triggerShockFromNode(node, delta) {
        const cascadeDiff = propagateShock(
          node.id, delta, allNodes, nodeConnections, DRAIN_COEFF, LAYER_WEIGHT, ATTENUATION_2ND, 3
        );
        const result = {
          originId:    node.id,
          originLabel: node.label,
          originLayer: node.layer,
          delta,
          cascade:     cascadeDiff,
          timestamp:   Date.now(),
        };
        shockResultRef.current = result;
        setShockResult(result);
        setShockPanelVisible(true);
        shockAnimator.start(node.id, delta, cascadeDiff, () => {
          nodeObjects.forEach((mesh, i) => {
            const n = allNodes[i];
            mesh.material.color.copy(n.layerColor);
            mesh.material.emissive.copy(n.layerColor);
            mesh.material.emissiveIntensity = 0.35;
            mesh.scale.setScalar(mesh.userData.baseScale ?? 1.0);
            if (mesh.userData.glowMesh) {
              mesh.userData.glowMesh.material.color.copy(n.layerColor);
              mesh.userData.glowMesh.material.opacity = mesh.userData.baseGlowOpacity ?? 0.04;
            }
          });
        });
      }

      triggerShockRef.current = triggerShockFromNode;

      // Compute total graph budget for normalizing individual node scores
      // Uses effective_cost so overlay inflation is reflected in the denominator
      const GRAPH_BUDGET = { attention: 0, energy: 0, money: 0 };
      allNodes.forEach(n => {
        GRAPH_BUDGET.attention += (n.effective_cost?.attention || 0);
        GRAPH_BUDGET.energy += (n.effective_cost?.energy || 0);
        GRAPH_BUDGET.money += (n.effective_cost?.money || 0);
      });

      // Baseline world budget — always uses raw node.cost (never modified by overlay)
      const WORLD_BUDGET = { attention: 0, energy: 0, money: 0 };
      allNodes.forEach(n => {
        WORLD_BUDGET.attention += (n.cost?.attention || 0);
        WORLD_BUDGET.energy += (n.cost?.energy || 0);
        WORLD_BUDGET.money += (n.cost?.money || 0);
      });

      // For each node, compute:
      //   baseScore   — own cost vector
      //   inboundLoad — cost pushed into this node from neighbors (depth-1 + depth-2)
      //   netScore    — base + inboundLoad (clamped 0–1)
      //   pressureSources — which neighbors are the highest-cost inbound pressures
      function computeNodeScore(node) {
        // V3 engine — overlay-aware. Uses effective_cost (post-overlay merge)
        // not raw base cost, so personal character sheet is reflected in scores.
        //
        // Returns both frames:
        //   pressureScore    — absolute (0–100), where this node sits in full graph
        //   totalLoadRatio   — personal (0–100%), share of the active budget

        const base = {
          attention: node.effective_cost?.attention || 0,
          energy: node.effective_cost?.energy || 0,
          money: node.effective_cost?.money || 0,
        };

        const inbound = { attention: 0, energy: 0, money: 0 };
        const outbound = { attention: 0, energy: 0, money: 0 };
        const pressureSources = [];

        const connections = nodeConnections[node.id] || [];
        const layerW = LAYER_WEIGHT[node.layer] || 0.5;

        connections.forEach(({ link, otherNode }) => {
          const coeff = DRAIN_COEFF[link.type] ?? 0.05;
          const strength = link.strength || 0.5;
          // Use neighbor's effective_cost so overlay propagates through edges
          const otherCost = otherNode.effective_cost || otherNode.cost || {};
          const otherLayerW = LAYER_WEIGHT[otherNode.layer] || 0.5;

          const targetId = typeof link.target === 'object' ? link.target?.id : link.target;
          const sourceId = typeof link.source === 'object' ? link.source?.id : link.source;
          const iAmTarget = !link.directed || targetId === node.id;
          const iAmSource = !link.directed || sourceId === node.id;

          if (iAmTarget) {
            const delta = {
              attention: (otherCost.attention || 0) * coeff * strength * otherLayerW,
              energy: (otherCost.energy || 0) * coeff * strength * otherLayerW,
              money: (otherCost.money || 0) * coeff * strength * otherLayerW,
            };
            inbound.attention += delta.attention;
            inbound.energy += delta.energy;
            inbound.money += delta.money;
            const totalDelta = delta.attention + delta.energy + delta.money;
            if (Math.abs(totalDelta) > 0.01) {
              pressureSources.push({
                nodeId: otherNode.id, label: otherNode.label,
                layer: otherNode.layer, contribution: totalDelta, type: link.type,
              });
            }
          }

          if (iAmSource) {
            outbound.attention += (base.attention || 0) * coeff * strength * otherLayerW;
            outbound.energy += (base.energy || 0) * coeff * strength * otherLayerW;
            outbound.money += (base.money || 0) * coeff * strength * otherLayerW;
          }
        });

        const scale = 3.5;
        const net = {
          attention: Math.min(1, Math.max(0,
            (base.attention + inbound.attention * layerW * 0.4 + outbound.attention * 0.5) / scale)),
          energy: Math.min(1, Math.max(0,
            (base.energy + inbound.energy * layerW * 0.4 + outbound.energy * 0.5) / scale)),
          money: Math.min(1, Math.max(0,
            (base.money + inbound.money * layerW * 0.4 + outbound.money * 0.5) / scale)),
        };

        const pressureScore = Math.round(
          ((net.attention * 0.4) + (net.energy * 0.35) + (net.money * 0.25)) * 100
        );

        pressureSources.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

        // Absolute frame: this node vs. total graph budget
        const budgetShare = {
          attention: GRAPH_BUDGET.attention > 0 ? (base.attention / GRAPH_BUDGET.attention) * 100 : 0,
          energy: GRAPH_BUDGET.energy > 0 ? (base.energy / GRAPH_BUDGET.energy) * 100 : 0,
          money: GRAPH_BUDGET.money > 0 ? (base.money / GRAPH_BUDGET.money) * 100 : 0,
        };

        // Personal frame: this node vs. active-only budget (character sheet)
        // In baseline mode ACTIVE_BUDGET == GRAPH_BUDGET, so ratio is the same.
        // With overlay active, inactive nodes are excluded → budget shrinks → ratio rises.
        const loadRatio = {
          attention: ACTIVE_BUDGET.attention > 0 ? (base.attention / ACTIVE_BUDGET.attention) * 100 : 0,
          energy: ACTIVE_BUDGET.energy > 0 ? (base.energy / ACTIVE_BUDGET.energy) * 100 : 0,
          money: ACTIVE_BUDGET.money > 0 ? (base.money / ACTIVE_BUDGET.money) * 100 : 0,
        };
        const totalLoadRatio = Math.round(
          loadRatio.attention * 0.4 + loadRatio.energy * 0.35 + loadRatio.money * 0.25
        );

        return {
          base, inbound, outbound, net,
          pressureScore, pressureSources: pressureSources.slice(0, 4),
          budgetShare, loadRatio, totalLoadRatio, layerW,
          overlayActive: node.overlay_active,
          overlaySource: node.overlay_source,
          overlayNote: node.overlay_note,
          overlayOn: OVERLAY_ON,
        };
      }

      // Same engine as computeNodeScore but uses raw node.cost and WORLD_BUDGET —
      // provides the baseline reference score used for delta display in personal mode.
      function computeWorldScore(node) {
        const base = {
          attention: node.cost?.attention || 0,
          energy: node.cost?.energy || 0,
          money: node.cost?.money || 0,
        };
        const inbound = { attention: 0, energy: 0, money: 0 };
        const outbound = { attention: 0, energy: 0, money: 0 };
        const connections = nodeConnections[node.id] || [];
        const layerW = LAYER_WEIGHT[node.layer] || 0.5;
        connections.forEach(({ link, otherNode }) => {
          const coeff = DRAIN_COEFF[link.type] ?? 0.05;
          const strength = link.strength || 0.5;
          const otherCost = otherNode.cost || {};
          const otherLayerW = LAYER_WEIGHT[otherNode.layer] || 0.5;
          const targetId = typeof link.target === 'object' ? link.target?.id : link.target;
          const sourceId = typeof link.source === 'object' ? link.source?.id : link.source;
          const iAmTarget = !link.directed || targetId === node.id;
          const iAmSource = !link.directed || sourceId === node.id;
          if (iAmTarget) {
            inbound.attention += (otherCost.attention || 0) * coeff * strength * otherLayerW;
            inbound.energy += (otherCost.energy || 0) * coeff * strength * otherLayerW;
            inbound.money += (otherCost.money || 0) * coeff * strength * otherLayerW;
          }
          if (iAmSource) {
            outbound.attention += (base.attention || 0) * coeff * strength * otherLayerW;
            outbound.energy += (base.energy || 0) * coeff * strength * otherLayerW;
            outbound.money += (base.money || 0) * coeff * strength * otherLayerW;
          }
        });
        const scale = 3.5;
        const net = {
          attention: Math.min(1, Math.max(0, (base.attention + inbound.attention * layerW * 0.4 + outbound.attention * 0.5) / scale)),
          energy: Math.min(1, Math.max(0, (base.energy + inbound.energy * layerW * 0.4 + outbound.energy * 0.5) / scale)),
          money: Math.min(1, Math.max(0, (base.money + inbound.money * layerW * 0.4 + outbound.money * 0.5) / scale)),
        };
        return {
          pressureScore: Math.round((net.attention * 0.4 + net.energy * 0.35 + net.money * 0.25) * 100),
          net,
          baseCost: base,
        };
      }

      // Builds the full score block HTML. worldScore is passed when overlay is active
      // so the panel can show world→personal deltas on each metric.
      function renderScoreHTML(score, worldScore) {
        const pressureColor = score.pressureScore >= 70 ? '#ff6b6b' : score.pressureScore >= 40 ? '#ffb347' : '#6bcb77';
        const pressureLabel = score.pressureScore >= 70 ? 'High' : score.pressureScore >= 40 ? 'Moderate' : 'Low';
        const loadColor = score.totalLoadRatio >= 20 ? '#ff6b6b' : score.totalLoadRatio >= 10 ? '#ffb347' : '#6bcb77';
        const loadLabel = score.totalLoadRatio >= 20 ? 'Major share' : score.totalLoadRatio >= 10 ? 'Significant' : 'Minor share';
        const scoreDelta = worldScore ? score.pressureScore - worldScore.pressureScore : 0;
        const deltaColor = scoreDelta > 0 ? '#ff6b6b' : scoreDelta < 0 ? '#6bcb77' : 'rgba(255,255,255,0.3)';
        const deltaStr = scoreDelta !== 0 ? `${scoreDelta > 0 ? '+' : ''}${scoreDelta}` : '';

        return `<div class="rl-score-block">
          <div class="rl-score-header">
            <span class="rl-score-label">Graph pressure</span>
            ${worldScore && scoreDelta !== 0
            ? `<span class="rl-score-world">${worldScore.pressureScore}</span><span class="rl-score-arrow">→</span>`
            : ''}
            <span class="rl-score-value" style="color:${pressureColor}">${score.pressureScore}</span>
            ${deltaStr ? `<span class="rl-score-delta" style="color:${deltaColor}">${deltaStr}</span>` : ''}
            <span class="rl-score-tag" style="color:${pressureColor}">${pressureLabel}</span>
          </div>
          <div class="rl-score-bars">
            ${[
            { key: 'attention', label: 'ATT', color: '#a78bfa' },
            { key: 'energy', label: 'NRG', color: '#34d399' },
            { key: 'money', label: 'MON', color: '#fbbf24' },
          ].map(({ key, label, color }) => {
            const netVal = score.net[key];
            const worldBar = worldScore ? Math.min(100, worldScore.net[key] * 100) : 0;
            const netBar = Math.min(100, netVal * 100);
            const barDelta = netBar - worldBar;
            const baseBar = Math.min(100, (score.base[key] / 3.5) * 100);
            return `<div class="rl-score-row">
                <span class="rl-score-res" style="color:${color}">${label}</span>
                <div class="rl-score-track">
                  ${worldScore
                ? `<div class="rl-score-world-fill"  style="width:${worldBar}%; background:${color}33"></div>
                       ${barDelta > 0
                  ? `<div class="rl-score-personal-add" style="left:${worldBar}%; width:${barDelta}%; background:${color}cc"></div>`
                  : `<div class="rl-score-personal-sub" style="width:${netBar}%; background:${color}cc"></div>`}`
                : `<div class="rl-score-base"    style="width:${baseBar}%; background:${color}44"></div>
                       <div class="rl-score-inbound" style="width:${netBar}%; background:${color}cc"></div>`}
                </div>
                <span class="rl-score-pct">${Math.round(netVal * 100)}%</span>
                ${worldScore && Math.abs(barDelta) > 0.5
                ? `<span class="rl-score-bar-delta" style="color:${barDelta > 0 ? '#ff6b6b88' : '#6bcb7788'}">${barDelta > 0 ? '+' : ''}${Math.round(barDelta)}%</span>`
                : ''}
              </div>`;
          }).join('')}
          </div>
          <div class="rl-load-divider"></div>
          <div class="rl-score-header">
            <span class="rl-score-label">Personal load${score.overlayOn ? '' : ' <span class="rl-baseline-tag">baseline</span>'}</span>
            <span class="rl-score-value" style="color:${loadColor}">${score.totalLoadRatio}%</span>
            <span class="rl-score-tag" style="color:${loadColor}">${loadLabel}</span>
          </div>
          <div class="rl-load-subtext">of active budget · ${[
            ['ATT', score.loadRatio?.attention ?? 0, '#a78bfa'],
            ['NRG', score.loadRatio?.energy ?? 0, '#34d399'],
            ['MON', score.loadRatio?.money ?? 0, '#fbbf24'],
          ].map(([l, v, c]) => `<span style="color:${c}">${l} ${v.toFixed(1)}%</span>`).join(' · ')}</div>
          ${score.overlayOn && score.overlaySource ? `
            <div class="rl-ov-source">
              <span class="rl-ov-badge">★</span> overridden via ${score.overlaySource}
              ${score.overlayNote ? `<span class="rl-ov-note"> — ${score.overlayNote}</span>` : ''}
            </div>` : ''}
          ${score.pressureSources?.length > 0 ? `
            <div class="rl-load-divider"></div>
            <div class="rl-pressure-label">Top inbound pressures</div>
            <ul class="rl-pressure-list">
              ${score.pressureSources.map(s => `<li>
                <span class="rl-conn-badge rl-conn-type-${s.type}">${s.type}</span>
                <span class="rl-pressure-name">${s.label}</span>
                <span class="rl-pressure-layer">${LAYERS[s.layer]?.label || s.layer}</span>
                <span class="rl-pressure-delta ${s.contribution > 0 ? 'pos' : 'neg'}">${s.contribution > 0 ? '+' : ''}${s.contribution.toFixed(2)}</span>
              </li>`).join('')}
            </ul>` : ''}
        </div>`;
      }

      // ─── Labels ─────────────────────────────────────────────────────
      const labelSprites = [];
      allNodes.forEach((node, i) => {
        const sprite = makeSprite(node.label, node.layerColor);
        sprite.scale.set(110, 18, 1);
        sprite.material.opacity = 0.88;
        const mesh = nodeObjects[i];
        sprite.position.set(mesh.position.x, mesh.position.y + mesh.userData.size + 16, mesh.position.z);
        scene.add(sprite);
        labelSprites.push(sprite);
      });

      // ─── Layer planes + axes ─────────────────────────────────────────
      Object.entries(LAYERS).forEach(([, layer]) => {
        const col = layer._color;
        const z = layer.zOffset;

        const plane = new THREE.Mesh(
          new THREE.PlaneGeometry(1600, 1600),
          new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.015, side: THREE.DoubleSide, depthWrite: false })
        );
        plane.position.z = z;
        scene.add(plane);
        const grid = new THREE.GridHelper(1300, 13, col, col);
        const gMat = Array.isArray(grid.material) ? grid.material[0] : grid.material;
        gMat.transparent = true; gMat.opacity = 0.06;
        grid.rotation.x = Math.PI / 2; grid.position.z = z;
        scene.add(grid);
        const border = new THREE.LineSegments(
          new THREE.EdgesGeometry(new THREE.PlaneGeometry(1350, 1350)),
          new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.18 })
        );
        border.position.z = z;
        scene.add(border);

        const axLen = AXIS_SCALE * 1.05;
        const axMat = new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.35 });
        const xLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(-axLen, 0, z), new THREE.Vector3(axLen, 0, z)]), axMat.clone());
        const yLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, -axLen, z), new THREE.Vector3(0, axLen, z)]), axMat.clone());
        scene.add(xLine); scene.add(yLine);

        const ao = { fontSize: 75, bold: false, bgAlpha: 0.38, w: 360, h: 60 };
        const addAxisLabel = (text, x, y) => {
          const s = makeSprite(text, col, ao);
          s.scale.set(90, 14, 1); s.material.opacity = 0.75;
          s.position.set(x, y, z); scene.add(s);
        };
        const ax = layer.axes;
        addAxisLabel(ax.x.neg, -axLen - 28, 6);
        addAxisLabel(ax.x.pos, axLen + 28, 6);
        addAxisLabel(ax.y.neg, 6, -axLen - 18);
        addAxisLabel(ax.y.pos, 6, axLen + 18);

        const nm = makeSprite(layer.label, col, { fontSize: 18, bgAlpha: 0, w: 480, h: 56 });
        nm.scale.set(130, 14, 1); nm.material.opacity = 0.28;
        nm.position.set(0, axLen + 55, z); scene.add(nm);
      });

      // ─── Camera state ────────────────────────────────────────────────
      let camTheta = 0, camPhi = 0.1, camRadius = 1200;
      let targetTheta = 0, targetPhi = 0.1, targetRadius = 1200;
      const panTarget = new THREE.Vector3();
      const panSmooth = new THREE.Vector3();
      let preFocusRadius = null;
      const preFocusPan = new THREE.Vector3();

      // ─── Input ──────────────────────────────────────────────────────
      let mouseButton = -1;
      let prevMouse = { x: 0, y: 0 };
      let dragDist = 0;
      const keys = {};

      renderer.domElement.addEventListener('contextmenu', e => e.preventDefault());

      renderer.domElement.addEventListener('mousedown', e => {
        mouseButton = e.button;
        prevMouse = { x: e.clientX, y: e.clientY };
        dragDist = 0;
      });

      renderer.domElement.addEventListener('mouseup', e => {
        if (dragDist < 5 && mouseButton === 0) handleClick(e);
        mouseButton = -1;
      });

      renderer.domElement.addEventListener('mousemove', e => {
        const dx = e.clientX - prevMouse.x;
        const dy = e.clientY - prevMouse.y;
        dragDist += Math.abs(dx) + Math.abs(dy);
        if (mouseButton === 0 || mouseButton === 2) {
          targetTheta -= dx * 0.005;
          targetPhi = Math.max(-1.3, Math.min(1.3, targetPhi - dy * 0.005));
        }
        prevMouse = { x: e.clientX, y: e.clientY };
        if (selectedNodeId === null) updateTooltip(e.clientX, e.clientY);
      });

      renderer.domElement.addEventListener('wheel', e => {
        targetRadius = Math.max(150, Math.min(3500, targetRadius + e.deltaY * 0.6));
      });

      onKeyDown = e => {
        keys[e.code] = true;
        if (e.code === 'Escape') clearSelection();
      };
      onKeyUp = e => { keys[e.code] = false; };
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);

      function applyKeyboard() {
        if (!Object.values(keys).some(Boolean)) return;
        const speed = camRadius * 0.006;
        const fwd = new THREE.Vector3(
          panSmooth.x - camera.position.x, 0, panSmooth.z - camera.position.z
        );
        if (fwd.lengthSq() < 0.001) fwd.set(0, 0, -1);
        fwd.normalize();
        const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), fwd).normalize();
        if (keys['KeyW'] || keys['ArrowUp']) panTarget.addScaledVector(fwd, speed);
        if (keys['KeyS'] || keys['ArrowDown']) panTarget.addScaledVector(fwd, -speed);
        if (keys['KeyA'] || keys['ArrowLeft']) panTarget.addScaledVector(right, speed);
        if (keys['KeyD'] || keys['ArrowRight']) panTarget.addScaledVector(right, -speed);
        if (keys['KeyQ']) panTarget.y += speed;
        if (keys['KeyE']) panTarget.y -= speed;
        if (keys['Equal'] || keys['NumpadAdd']) targetRadius = Math.max(150, targetRadius - speed * 3);
        if (keys['Minus'] || keys['NumpadSubtract']) targetRadius = Math.min(3500, targetRadius + speed * 3);
      }

      // Touch
      let lastTouchDist = null;
      renderer.domElement.addEventListener('touchstart', e => {
        if (e.touches.length === 1) { prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY }; dragDist = 0; mouseButton = 0; }
        if (e.touches.length === 2) { mouseButton = -1; lastTouchDist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY); }
      }, { passive: true });
      renderer.domElement.addEventListener('touchend', () => { mouseButton = -1; lastTouchDist = null; }, { passive: true });
      renderer.domElement.addEventListener('touchmove', e => {
        if (e.touches.length === 1 && mouseButton === 0) {
          const dx = e.touches[0].clientX - prevMouse.x, dy = e.touches[0].clientY - prevMouse.y;
          dragDist += Math.abs(dx) + Math.abs(dy);
          targetTheta -= dx * 0.005;
          targetPhi = Math.max(-1.3, Math.min(1.3, targetPhi - dy * 0.005));
          prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
        if (e.touches.length === 2) {
          const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
          if (lastTouchDist !== null) targetRadius = Math.max(150, Math.min(3500, targetRadius - (dist - lastTouchDist) * 2));
          lastTouchDist = dist;
        }
      }, { passive: true });

      // ─── Tooltip ────────────────────────────────────────────────────
      const raycaster = new THREE.Raycaster();
      const mouse2D = new THREE.Vector2();
      const tooltip = tooltipRef.current;

      function getNDC(cx, cy) {
        const rect = renderer.domElement.getBoundingClientRect();
        return {
          x: ((cx - rect.left) / rect.width) * 2 - 1,
          y: -((cy - rect.top) / rect.height) * 2 + 1,
        };
      }

      function updateTooltip(cx, cy) {
        const { x, y } = getNDC(cx, cy);
        mouse2D.set(x, y);
        raycaster.setFromCamera(mouse2D, camera);
        const hits = raycaster.intersectObjects(nodeObjects);
        if (hits.length > 0) {
          const { node } = hits[0].object.userData;
          if (node.overlay_active === false) { tooltip.style.display = 'none'; return; }
          tooltip.style.display = 'block';
          tooltip.style.left = (cx + 14) + 'px';
          tooltip.style.top = (cy - 12) + 'px';
          const costs = Object.entries(node.cost || {}).filter(([, v]) => v > 0);
          tooltip.innerHTML = `
            <strong>${node.label}</strong>
            <span class="rl-layer-tag">${LAYERS[node.layer]?.label} · ${node.type}</span>
            ${node.note || ''}
            ${costs.length ? `<span class="rl-cost-bar">${costs.map(([k, v]) => costBar(k, v)).join('')}</span>` : ''}
          `;
        } else {
          tooltip.style.display = 'none';
        }
      }

      function costBar(label, val) {
        const pct = Math.round(val * 100);
        return `<span class="rl-cb">${label} <span class="rl-cb-fill" style="width:${pct * 0.65}px"></span>${pct}%</span>`;
      }

      // ─── Selection ──────────────────────────────────────────────────
      let selectedNodeId = null;
      let activeLayer = 'all';

      function handleClick(e) {
        const { x, y } = getNDC(e.clientX, e.clientY);
        mouse2D.set(x, y);
        raycaster.setFromCamera(mouse2D, camera);
        const hits = raycaster.intersectObjects(nodeObjects);
        if (hits.length > 0) {
          const { node } = hits[0].object.userData;
          selectedNodeId === node.id ? clearSelection() : selectNode(node);
        } else {
          clearSelection();
        }
      }

      function selectNode(node) {
        // Desktop shock intercept — skip panel entirely, just fire and highlight
        if (shockModeRef.current && !isMobileLayout) {
          setSelectedShockNode(node.label);
          triggerShockFromNode(node, shockDeltaRef.current);
          nodeObjects.forEach((mesh, i) => {
            mesh.material.opacity = allNodes[i].id === node.id ? 1.0 : 0.12;
            mesh.material.emissiveIntensity = allNodes[i].id === node.id ? 1.0 : 0.0;
            mesh.scale.setScalar(allNodes[i].id === node.id ? 1.4 : 1.0);
          });
          return;
        }
        // Mobile shock: track node but fall through to populate all detail refs
        if (shockModeRef.current && isMobileLayout) {
          mobileShockNodeRef.current = node;
        }
        selectedNodeId = node.id;
        tooltip.style.display = 'none';
        if (isMobileLayout && !shockModeRef.current) {
          preFocusRadius = targetRadius;
          preFocusPan.copy(panTarget);
          targetRadius = Math.max(400, camRadius * 0.25);
          panTarget.set(node.x, node.y - 90, node.z);
        }
        const connectedIds = new Set(nodeConnections[node.id].map(c => c.otherNode.id));

        // BFS depth-2: neighbors of neighbors, excluding selected + 1st-degree
        const secondDegreeIds = new Set();
        connectedIds.forEach(neighborId => {
          nodeConnections[neighborId].forEach(({ otherNode }) => {
            if (otherNode.id !== node.id && !connectedIds.has(otherNode.id)) {
              secondDegreeIds.add(otherNode.id);
            }
          });
        });

        nodeObjects.forEach((mesh, i) => {
          const n = allNodes[i];
          const isSel = n.id === node.id;
          const isNeighbor = connectedIds.has(n.id);
          const is2nd = secondDegreeIds.has(n.id);
          mesh.material.opacity = isSel ? 1.0 : isNeighbor ? 0.75 : is2nd ? 0.3 : 0.05;
          mesh.material.emissiveIntensity = isSel ? 0.8 : isNeighbor ? 0.4 : is2nd ? 0.15 : 0.0;
          mesh.scale.setScalar(isSel ? 1.35 : 1.0);
        });
        labelSprites.forEach((s, i) => {
          const n = allNodes[i];
          s.material.opacity = (n.id === node.id || connectedIds.has(n.id)) ? 0.95 : secondDegreeIds.has(n.id) ? 0.45 : 0.04;
        });
        linkObjects.forEach(({ line, link, sourceNode, targetNode, cone }) => {
          const touchesSel = sourceNode.id === node.id || targetNode.id === node.id;
          const touches2nd = (connectedIds.has(sourceNode.id) && secondDegreeIds.has(targetNode.id)) ||
            (connectedIds.has(targetNode.id) && secondDegreeIds.has(sourceNode.id));
          let lineOpacity, coneOpacity;
          if (touchesSel) {
            lineOpacity = 0.9;
            coneOpacity = 0.95;
            line.material.color.set(LINK_COLOR[link.type] || 0xffffff);
            if (cone) cone.material.color.set(LINK_COLOR[link.type] || 0xffffff);
          } else if (touches2nd) {
            lineOpacity = 0.35;
            coneOpacity = 0.4;
            line.material.color.set(LINK_COLOR[link.type] || 0xffffff);
            if (cone) cone.material.color.set(LINK_COLOR[link.type] || 0xffffff);
          } else {
            lineOpacity = 0.02;
            coneOpacity = 0.02;
          }
          line.material.opacity = lineOpacity;
          if (cone) cone.material.opacity = coneOpacity;
        });

        const conns = nodeConnections[node.id];
        const layerInfo = LAYERS[node.layer];
        const pos = node.pos || { x: 0, y: 0 };
        const axes = layerInfo?.axes;

        selTitleRef.current.textContent = node.label;
        selLayerRef.current.textContent = `${layerInfo?.label || ''} · ${node.type}`;
        selNoteRef.current.textContent = node.note || '';
        selAxesRef.current.innerHTML = axes ? `
          <div class="rl-axis-pos"><span>${axes.x.neg} ↔ ${axes.x.pos}</span><span class="rl-axis-val">${pos.x > 0 ? '+' : ''}${(Math.round(pos.x * 10) / 10)}</span></div>
          <div class="rl-axis-pos"><span>${axes.y.neg} ↔ ${axes.y.pos}</span><span class="rl-axis-val">${pos.y > 0 ? '+' : ''}${(Math.round(pos.y * 10) / 10)}</span></div>
        ` : '';
        selCostsRef.current.innerHTML = Object.entries(node.effective_cost || node.cost || {})
          .filter(([, v]) => v > 0)
          .map(([k, v]) => {
            const worldCost = node.cost?.[k] || 0;
            const costDelta = v - worldCost;
            const isOv = OVERLAY_ON && Math.abs(costDelta) > 0.001;
            return `<div class="rl-cost-row">
              <span>${k}</span>
              <div class="rl-cost-track">
                <div class="rl-cost-fill" style="width:${isOv ? worldCost * 100 : v * 100}%; background:rgba(255,255,255,${isOv ? '0.15' : '0.35'})"></div>
                ${isOv && costDelta > 0 ? `<div class="rl-cost-fill-personal" style="left:${worldCost * 100}%; width:${costDelta * 100}%; background:rgba(255,120,120,0.5)"></div>` : ''}
              </div>
              <span>${Math.round(v * 100)}%${isOv ? ` <span class="rl-cost-delta" style="color:${costDelta > 0 ? '#ff8888' : '#6bcb77'}">${costDelta > 0 ? '+' : ''}${Math.round(costDelta * 100)}%</span>` : ''}</span>
            </div>`;
          })
          .join('');

        // ── Phase 2: Compute and render node score ──────────────────────
        const score = computeNodeScore(node);
        const worldScore = OVERLAY_ON ? computeWorldScore(node) : null;
        if (selScoreRef.current) selScoreRef.current.innerHTML = renderScoreHTML(score, worldScore);
        selCountRef.current.textContent = `${conns.length} connection${conns.length !== 1 ? 's' : ''}`;
        selConnectionsRef.current.innerHTML = conns.map(({ link, otherNode }) => {
          const isSrcMe = (typeof link.source === 'string' ? link.source : link.source?.id) === node.id;
          const dirSymbol = !link.directed ? '↔' : isSrcMe ? '→' : '←';
          const dirClass = link.directed ? 'rl-conn-dir rl-conn-dir--directed' : 'rl-conn-dir';
          return `<li>
            <span class="${dirClass}">${dirSymbol}</span>
            <strong>${otherNode.label}</strong>
            <span class="rl-layer-tag">${LAYERS[otherNode.layer]?.label}</span>
            <span class="rl-conn-badge rl-conn-type-${link.type}">${link.type}</span>
            ${link.note ? `<span class="rl-conn-note">${link.note}</span>` : ''}
          </li>`;
        }).join('');
        selPanelRef.current.style.display = 'block';

        // Mobile shock: fire shock from this node and enter shock view
        if (shockModeRef.current && isMobileLayout) {
          setSelectedShockNode(node.label);
          triggerShockFromNode(node, mobileShockDeltaRef.current);
          setMobileShockView(true);
        }
      }

      // Returns baseline opacity for a link, accounting for layer filter and overlay dimming.
      // Links where either endpoint is overlay-dimmed (inactive) go nearly invisible.
      function getBaseLinkOpacity(link, sourceNode, targetNode, layerKey) {
        const show = layerKey === 'all' || sourceNode.layer === layerKey || targetNode.layer === layerKey;
        if (!show) return 0.01;
        if (OVERLAY_ON && (!sourceNode.overlay_active || !targetNode.overlay_active)) return 0.01;
        const style = LINK_STYLE[link.type] || LINK_STYLE.neutral;
        return link.crossLayer ? style.opacity : style.dimOpacity;
      }

      // Apply baseline opacity/emissive to a mesh, respecting layer visibility and overlay dimming.
      // Also dims the glow halo and label sprite at the same index.
      function applyBaseOpacity(mesh, i, layerKey) {
        const visible = layerKey === 'all' || allNodes[i].layer === layerKey;
        const dimmed = mesh.userData.overlayDimmed;
        mesh.material.opacity = !visible ? 0.04 : dimmed ? 0.08 : 0.9;
        mesh.material.emissiveIntensity = !visible ? 0.0 : dimmed ? 0.0 : 0.35;
        if (mesh.userData.glowMesh) {
          mesh.userData.glowMesh.material.opacity =
            !visible ? 0.0 : dimmed ? 0.0 : mesh.userData.baseGlowOpacity ?? 0.04;
        }
        if (labelSprites[i]) {
          labelSprites[i].material.opacity = !visible ? 0.04 : dimmed ? 0.0 : 0.88;
        }
      }

      function clearSelection() {
        selectedNodeId = null;
        if (isMobileLayout && preFocusRadius !== null) {
          targetRadius = preFocusRadius;
          panTarget.copy(preFocusPan);
          preFocusRadius = null;
        }
        if (selPanelRef.current) selPanelRef.current.style.display = 'none';
        if (selScoreRef.current) selScoreRef.current.innerHTML = '';
        if (tooltip) tooltip.style.display = 'none';

        nodeObjects.forEach((mesh, i) => {
          applyBaseOpacity(mesh, i, activeLayer);
          mesh.scale.setScalar(mesh.userData.baseScale ?? 1.0);
        });
        linkObjects.forEach(({ line, link, sourceNode, targetNode, cone }) => {
          const op = getBaseLinkOpacity(link, sourceNode, targetNode, activeLayer);
          line.material.opacity = op;
          if (cone) cone.material.opacity = op;
        });
      }

      clearSelRef.current = clearSelection;

      // ─── Layer filter ────────────────────────────────────────────────
      layerClickRef.current = (layerKey) => {
        clearSelection();
        activeLayer = layerKey;

        Object.entries(layerBtnsRef.current).forEach(([key, btn]) => {
          if (!btn) return;
          const active = key === layerKey;
          btn.style.background = active ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)';
          btn.style.borderColor = active ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.1)';
          btn.style.color = active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)';
        });

        const info = LAYERS[activeLayer];
        if (layerLabelRef.current) layerLabelRef.current.textContent = info ? info.label : 'All Layers';
        if (layerDescRef.current) layerDescRef.current.textContent = info ? info.description : 'The full topology of a life';

        nodeObjects.forEach((mesh, i) => applyBaseOpacity(mesh, i, activeLayer));
        linkObjects.forEach(({ line, link, sourceNode, targetNode, cone }) => {
          const op = getBaseLinkOpacity(link, sourceNode, targetNode, activeLayer);
          line.material.opacity = op;
          if (cone) cone.material.opacity = op;
        });
      };

      // ─── Overlay re-apply ───────────────────────────────────────────
      // Called by quiz submit and world/personal toggle. Mutates the local
      // overlay vars (OVERLAY_ON, OV_OVERRIDES), re-stamps effective_cost on
      // every node, recomputes both budgets, and refreshes node sphere scales.
      function reapplyOverlay(newOverlay) {
        OVERLAY.active = newOverlay.active;
        OVERLAY.overrides = newOverlay.overrides || {};
        OVERLAY.source = newOverlay.source || null;
        OVERLAY_ON = OVERLAY.active === true;
        OV_OVERRIDES = OVERLAY.overrides;

        // Re-stamp effective_cost
        allNodes.forEach(node => {
          const ov = OV_OVERRIDES[node.id];
          // In personal mode: only explicitly answered nodes (ov exists + not deactivated) are active.
          // Unanswered nodes (no ov) are silent. In world mode all nodes are active.
          node.overlay_active = OVERLAY_ON
            ? (ov !== undefined && ov.active !== false)
            : true;
          if (!OVERLAY_ON || !ov) {
            node.effective_cost = { ...node.cost };
            node.overlay_source = null;
            node.overlay_note = null;
          } else {
            const mult = ov.weight_multiplier ?? 1.0;
            const costOv = ov.cost || {};
            node.effective_cost = {
              attention: costOv.attention ?? (node.cost?.attention || 0) * mult,
              energy: costOv.energy ?? (node.cost?.energy || 0) * mult,
              money: costOv.money ?? (node.cost?.money || 0) * mult,
            };
            node.overlay_source = ov.source || 'manual';
            node.overlay_note = ov.note || null;
          }
        });

        // Recompute budgets in-place so computeNodeScore closure picks up changes
        ACTIVE_BUDGET.attention = 0; ACTIVE_BUDGET.energy = 0; ACTIVE_BUDGET.money = 0;
        allNodes.forEach(n => {
          if (!n.overlay_active) return;
          const tot = (n.effective_cost?.attention || 0) + (n.effective_cost?.energy || 0) + (n.effective_cost?.money || 0);
          if (tot < 0.1) return;
          ACTIVE_BUDGET.attention += n.effective_cost?.attention || 0;
          ACTIVE_BUDGET.energy += n.effective_cost?.energy || 0;
          ACTIVE_BUDGET.money += n.effective_cost?.money || 0;
        });
        GRAPH_BUDGET.attention = 0; GRAPH_BUDGET.energy = 0; GRAPH_BUDGET.money = 0;
        allNodes.forEach(n => {
          GRAPH_BUDGET.attention += n.effective_cost?.attention || 0;
          GRAPH_BUDGET.energy += n.effective_cost?.energy || 0;
          GRAPH_BUDGET.money += n.effective_cost?.money || 0;
        });

        // Update sphere scales, overlay dimming flags, then apply opacity via shared helper
        nodeObjects.forEach((mesh, i) => {
          const node = allNodes[i];
          const ov = OVERLAY_ON ? OV_OVERRIDES[node.id] : null;
          const mult = ov?.weight_multiplier ?? 1.0;
          mesh.userData.baseScale = OVERLAY_ON ? Math.max(0.88, 1 + (mult - 1) * 0.12) : 1.0;
          mesh.userData.overlayDimmed = OVERLAY_ON && !node.overlay_active;
          applyBaseOpacity(mesh, i, activeLayer);
          mesh.scale.setScalar(mesh.userData.baseScale);
        });

        // Dim links whose endpoints are inactive
        linkObjects.forEach(({ line, link, sourceNode, targetNode, cone }) => {
          const op = getBaseLinkOpacity(link, sourceNode, targetNode, activeLayer);
          line.material.opacity = op;
          if (cone) cone.material.opacity = op;
        });

        // If a node is selected, refresh its panel cost + score display
        if (selectedNodeId !== null) {
          const node = allNodes.find(n => n.id === selectedNodeId);
          if (node) {
            selCostsRef.current.innerHTML = Object.entries(node.effective_cost || node.cost || {})
              .filter(([, v]) => v > 0)
              .map(([k, v]) => {
                const worldCost = node.cost?.[k] || 0;
                const costDelta = v - worldCost;
                const isOv = OVERLAY_ON && Math.abs(costDelta) > 0.001;
                return `<div class="rl-cost-row">
                  <span>${k}</span>
                  <div class="rl-cost-track">
                    <div class="rl-cost-fill" style="width:${isOv ? worldCost * 100 : v * 100}%; background:rgba(255,255,255,${isOv ? '0.15' : '0.35'})"></div>
                    ${isOv && costDelta > 0 ? `<div class="rl-cost-fill-personal" style="left:${worldCost * 100}%; width:${costDelta * 100}%; background:rgba(255,120,120,0.5)"></div>` : ''}
                  </div>
                  <span>${Math.round(v * 100)}%${isOv ? ` <span class="rl-cost-delta" style="color:${costDelta > 0 ? '#ff8888' : '#6bcb77'}">${costDelta > 0 ? '+' : ''}${Math.round(costDelta * 100)}%</span>` : ''}</span>
                </div>`;
              }).join('');
            const score = computeNodeScore(node);
            const worldScore = OVERLAY_ON ? computeWorldScore(node) : null;
            if (selScoreRef.current) selScoreRef.current.innerHTML = renderScoreHTML(score, worldScore);
          }
        }
      }

      applyOverlayRef.current = reapplyOverlay;

      // Restore persisted quiz overlay (e.g. after a page refresh)
      if (Object.keys(quizOverridesRef.current).length > 0) {
        reapplyOverlay({ active: true, overrides: quizOverridesRef.current, source: 'quiz' });
      }

      // ─── Animate ────────────────────────────────────────────────────
      let t = 0;
      function animate() {
        animId = requestAnimationFrame(animate);
        t += 0.0025;
        applyKeyboard();

        camTheta += (targetTheta - camTheta) * 0.06;
        camPhi += (targetPhi - camPhi) * 0.06;
        camRadius += (targetRadius - camRadius) * 0.06;
        panSmooth.lerp(panTarget, 0.06);

        camera.position.x = camRadius * Math.sin(camTheta) * Math.cos(camPhi) + panSmooth.x;
        camera.position.y = camRadius * Math.sin(camPhi) + panSmooth.y;
        camera.position.z = camRadius * Math.cos(camTheta) * Math.cos(camPhi) + panSmooth.z;
        camera.lookAt(panSmooth);

        nodeObjects.forEach((mesh, i) => {
          if (selectedNodeId !== null && allNodes[i].id === selectedNodeId) return;
          const bs = mesh.userData.baseScale ?? 1.0;
          mesh.scale.setScalar(bs * (1 + Math.sin(t + i * 0.7) * 0.04));
        });

        shockAnimator.tick(performance.now(), getMeshBaseState);
        composer.render();
      }
      animate();

      // ─── Resize ──────────────────────────────────────────────────────
      ro = new ResizeObserver(() => {
        camera.aspect = W() / H();
        camera.updateProjectionMatrix();
        renderer.setSize(W(), H());
        composer.setSize(W(), H());
      });
      ro.observe(container);

      // Hide loading overlay
      if (loadingRef.current) {
        loadingRef.current.style.transition = 'opacity 0.4s';
        loadingRef.current.style.opacity = '0';
        setTimeout(() => {
          if (loadingRef.current) loadingRef.current.style.display = 'none';
        }, 400);
      }
    };

    init();

    return () => {
      if (animId !== null) cancelAnimationFrame(animId);
      if (ro) ro.disconnect();
      if (onKeyDown) window.removeEventListener('keydown', onKeyDown);
      if (onKeyUp) window.removeEventListener('keyup', onKeyUp);
      if (renderer) {
        renderer.dispose();
        if (container && container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      }
      if (composer) composer.dispose();
      layerClickRef.current = null;
      clearSelRef.current = null;
    };
  }, []);

  // ─── Quiz handlers ──────────────────────────────────────────────
  const openQuiz = async () => {
    let data = quizData;
    if (!data) {
      try {
        data = await fetch('/data/quiz.json').then(r => r.json());
        setQuizData(data);
      } catch (e) {
        console.error('Failed to load quiz.json', e);
        return;
      }
    }
    setDomainIdx(0);
    setQuestionIdx(0);
    setAnswers({});
    setSelectedAnswer(null);
    setSelectedFollowup(null);
    setShowFollowup(false);
    setQuizOpen(true);
  };

  const submitQuiz = (finalAnswers) => {
    const merged = {};
    Object.values(finalAnswers).forEach(ans => {
      (ans.nodes || []).forEach(n => { merged[n.id] = { ...n }; });
    });
    quizOverridesRef.current = merged;
    try { window.localStorage.setItem(QUIZ_KEY, JSON.stringify({ overrides: merged })); } catch { }
    applyOverlayRef.current?.({ active: true, overrides: merged, source: 'quiz' });
    setOverlayMode('personal');
    setQuizOpen(false);
  };

  const advanceQuestion = (mainAns, followupAns) => {
    const domain = quizData.domains[domainIdx];
    const mainQs = domain.questions.filter(q => !q.parentQuestion);
    const currentQ = mainQs[questionIdx];
    const followupId = mainAns?.followup;
    const followupQ = followupId ? domain.questions.find(q => q.id === followupId) : null;

    const newAnswers = { ...answers };
    if (mainAns) newAnswers[currentQ.id] = mainAns;
    if (followupAns && followupQ) newAnswers[followupQ.id] = followupAns;

    const isLastDomain = domainIdx === quizData.domains.length - 1;
    const isLastQuestion = questionIdx === mainQs.length - 1;

    if (isLastDomain && isLastQuestion) {
      submitQuiz(newAnswers);
      return;
    }

    setAnswers(newAnswers);
    setSelectedAnswer(null);
    setSelectedFollowup(null);
    setShowFollowup(false);
    if (questionIdx < mainQs.length - 1) {
      setQuestionIdx(q => q + 1);
    } else {
      setDomainIdx(d => d + 1);
      setQuestionIdx(0);
    }
  };

  const skipQuestion = () => advanceQuestion(null, null);

  const toggleOverlayMode = (mode) => {
    setOverlayMode(mode);
    if (mode === 'world') {
      try { window.localStorage.removeItem(QUIZ_KEY); } catch { }
      applyOverlayRef.current?.({ active: false, overrides: {}, source: null });
    } else {
      try { window.localStorage.setItem(QUIZ_KEY, JSON.stringify({ overrides: quizOverridesRef.current })); } catch { }
      applyOverlayRef.current?.({ active: true, overrides: quizOverridesRef.current, source: 'quiz' });
    }
  };

  // ─── Quiz derived values ─────────────────────────────────────────
  const currentDomain = quizData?.domains[domainIdx];
  const mainQuestions = currentDomain?.questions.filter(q => !q.parentQuestion) ?? [];
  const currentQ = mainQuestions[questionIdx] ?? null;
  const followupQ = (showFollowup && selectedAnswer?.followup && currentDomain)
    ? currentDomain.questions.find(q => q.id === selectedAnswer.followup) ?? null
    : null;
  const isLastDomain = quizData ? domainIdx === quizData.domains.length - 1 : false;
  const isLastQuestion = questionIdx === mainQuestions.length - 1;
  const isLastStep = isLastDomain && isLastQuestion;
  const canAdvance = selectedAnswer !== null && (!showFollowup || selectedFollowup !== null);
  const totalDomains = quizData?.domains.length ?? 0;
  const progressPct = totalDomains > 0
    ? ((domainIdx + (questionIdx + 1) / Math.max(1, mainQuestions.length)) / totalDomains) * 100
    : 0;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: location.pathname === '/viz/reality-layers' ? '100dvh' : '70vh',
        minHeight: location.pathname === '/viz/reality-layers' ? 'unset' : 480,
        background: '#030508',
        overflow: 'hidden',
        touchAction: location.pathname === '/viz/reality-layers' ? 'none' : 'auto',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: location.pathname === '/viz/reality-layers' ? 0 : 8,
      }}
    >
      {/* Loading overlay */}
      <div
        ref={loadingRef}
        style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.2)', zIndex: 100,
        }}
      >
        Loading
      </div>

      {/* Top-left info */}
      <div style={{ position: 'absolute', top: 24, left: 24, zIndex: 10, pointerEvents: overlayMode ? 'auto' : 'none' }}>
        <div ref={layerLabelRef} style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 5, pointerEvents: 'none' }}>
          All Layers
        </div>
        <div ref={layerDescRef} style={{ fontSize: 11, fontWeight: 300, color: 'rgba(255,255,255,0.35)', maxWidth: 220, lineHeight: 1.7, pointerEvents: 'none' }}>
          The full topology of a life
        </div>
        {overlayMode && (
          <div className="rl-mode-toggle">
            <button
              className={`rl-mode-btn${overlayMode === 'world' ? ' rl-mode-btn--active' : ''}`}
              onClick={() => toggleOverlayMode('world')}
            >World</button>
            <button
              className={`rl-mode-btn${overlayMode === 'personal' ? ' rl-mode-btn--active' : ''}`}
              onClick={() => toggleOverlayMode('personal')}
            >Personal</button>
          </div>
        )}
        {overlayMode === 'personal' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
            <button className="rl-retake-btn" onClick={openQuiz}>retake</button>
            <button className="rl-retake-btn" onClick={() => setProfileOpen(true)} style={{ color: 'rgba(139,92,246,0.8)', borderColor: 'rgba(139,92,246,0.3)' }}>profile</button>
          </div>
        )}
      </div>

      {/* Controls hint */}
      <div style={{ position: 'absolute', bottom: 72, right: 20, fontSize: 9, color: 'rgba(255,255,255,0.18)', textAlign: 'right', lineHeight: 2, pointerEvents: 'none', zIndex: 10, letterSpacing: '0.05em' }}>
        left drag — orbit<br />
        right drag — pan<br />
        wasd / arrows — pan<br />
        scroll — zoom<br />
        click node — select<br />
        esc — deselect
      </div>

      {/* Selection panel */}
      <div
        ref={selPanelRef}
        style={isMobileLayout ? {
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(4,6,14,0.95)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '10px 10px 0 0',
          padding: mobileShockView ? '12px 0 0' : '12px 16px 24px',
          zIndex: 15,
          display: 'none',
          backdropFilter: 'blur(14px)',
          maxHeight: sheetSnap === 'full' ? '88vh' : sheetSnap === 'peek' ? '22vh' : mobileShockView ? '65vh' : '45vh',
          overflowY: 'auto',
        } : {
          position: 'absolute', top: 24, right: 24,
          width: 250,
          background: 'rgba(4,6,14,0.9)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6,
          padding: 16,
          zIndex: 15,
          display: 'none',
          backdropFilter: 'blur(14px)',
          maxHeight: 'calc(100% - 100px)',
          overflowY: 'auto',
        }}
      >
        {/* Drag handle — mobile only */}
        {isMobileLayout && (
          <div
            ref={sheetHandleRef}
            style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)', margin: `0 auto ${mobileShockView ? 8 : 12}px`, touchAction: 'none', cursor: 'grab' }}
          />
        )}

        {/* Back button — mobile shock view only */}
        {isMobileLayout && mobileShockView && (
          <button
            onClick={() => setMobileShockView(false)}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 12, cursor: 'pointer', padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            ← Back
          </button>
        )}

        {/* ✕ close — hidden in mobile shock view (Back handles it) */}
        {!(isMobileLayout && mobileShockView) && (
          <button
            onClick={() => clearSelRef.current?.()}
            style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}
            title="Deselect (Esc)"
          >
            ✕
          </button>
        )}

        {/* Node name — always visible; add horizontal padding in shock view */}
        <div ref={selTitleRef} style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 2, paddingRight: 20, ...(isMobileLayout && mobileShockView ? { padding: '0 16px', paddingRight: 16 } : {}) }} />
        <div ref={selLayerRef} style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', marginBottom: mobileShockView ? 10 : 8, ...(isMobileLayout && mobileShockView ? { padding: '2px 16px 0' } : {}) }} />

        {/* Normal detail — hidden while mobile shock view is active */}
        <div ref={selNoteRef} style={{ fontSize: 11, fontWeight: 300, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, marginBottom: 12, display: isMobileLayout && mobileShockView ? 'none' : undefined }} />
        <div ref={selAxesRef} style={{ marginBottom: 12, display: isMobileLayout && mobileShockView ? 'none' : undefined }} />
        <div ref={selCostsRef} style={{ marginBottom: 12, display: isMobileLayout && mobileShockView ? 'none' : undefined }} />
        <div ref={selScoreRef} style={{ marginBottom: 12, display: isMobileLayout && mobileShockView ? 'none' : undefined }} />
        <div ref={selCountRef} style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.2)', marginBottom: 7, display: isMobileLayout && mobileShockView ? 'none' : undefined }} />
        <ul ref={selConnectionsRef} style={{ listStyle: 'none', padding: 0, fontSize: 11, color: 'rgba(255,255,255,0.5)', display: isMobileLayout && mobileShockView ? 'none' : undefined }} />

        {/* ── Mobile shock cascade diff ─────────────────────────────────── */}
        {isMobileLayout && mobileShockView && shockResult && (() => {
          const MOBILE_LAYER_COLORS = {
            physical: '#94a3b8', personal_physical: '#34d399', family: '#f472b6',
            spirituality: '#a78bfa', social: '#60a5fa', work: '#fbbf24',
            civic_government: '#f87171', entertainment: '#fb923c',
          };
          return (
            <div style={{ padding: '0 16px', paddingBottom: 72 }}>
              {shockResult.cascade.slice(0, 20).map((entry, i) => {
                const totalDelta = entry.deltaAttention + entry.deltaEnergy + entry.deltaMoney;
                const isPositive = totalDelta > 0;
                return (
                  <div key={entry.nodeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', width: 14, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.label}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 8, color: MOBILE_LAYER_COLORS[entry.layer] || '#fff', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{entry.layer.replace(/_/g, ' ')}</span>
                        {Math.abs(entry.deltaAttention) > 0.005 && <span style={{ fontSize: 8, color: '#a78bfa' }}>ATT {entry.deltaAttention > 0 ? '+' : ''}{Math.round(entry.deltaAttention * 100)}%</span>}
                        {Math.abs(entry.deltaEnergy)    > 0.005 && <span style={{ fontSize: 8, color: '#34d399' }}>NRG {entry.deltaEnergy    > 0 ? '+' : ''}{Math.round(entry.deltaEnergy    * 100)}%</span>}
                        {Math.abs(entry.deltaMoney)     > 0.005 && <span style={{ fontSize: 8, color: '#fbbf24' }}>MON {entry.deltaMoney     > 0 ? '+' : ''}{Math.round(entry.deltaMoney     * 100)}%</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: isPositive ? '#ff8888' : '#6bcb77', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {isPositive ? '+' : ''}{Math.round(totalDelta * 100)}%
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ── Mobile shock bottom bar — preset chips + magnitude slider + Fire ── */}
        {isMobileLayout && mobileShockView && (
          <div style={{ position: 'sticky', bottom: 0, background: 'rgba(4,6,14,0.98)', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px 16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Preset chips */}
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {[
                { label: 'Job loss',    magnitude: 0.8,  base: { attention: 0.75, energy: 0.5,  money: 1.0 } },
                { label: 'Bereavement', magnitude: 0.9,  base: { attention: 1.0,  energy: 0.7,  money: 0.1 } },
                { label: 'New partner', magnitude: 0.35, base: { attention: -0.9, energy: -0.6, money: 0.3 } },
                { label: 'Illness',     magnitude: 0.75, base: { attention: 0.6,  energy: 1.0,  money: 0.4 } },
              ].map(preset => (
                <button
                  key={preset.label}
                  onClick={() => { setMobileMagnitude(preset.magnitude); setMobileDeltaBase(preset.base); }}
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', padding: '4px 10px', fontSize: 10, borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {/* Magnitude slider + Fire */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>Δ</span>
              <input
                type="range" min="0" max="1" step="0.05"
                value={mobileMagnitude}
                onChange={e => setMobileMagnitude(parseFloat(e.target.value))}
                className="rl-shock-slider"
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 10, color: mobileMagnitude > 0.6 ? '#ff8888' : '#fbbf24', width: 30, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(mobileMagnitude * 100)}
              </span>
              <button
                onClick={() => {
                  const node = mobileShockNodeRef.current;
                  if (!node || !triggerShockRef.current) return;
                  const delta = {
                    attention: mobileMagnitude * mobileDeltaBase.attention,
                    energy:    mobileMagnitude * mobileDeltaBase.energy,
                    money:     mobileMagnitude * mobileDeltaBase.money,
                  };
                  mobileShockDeltaRef.current = delta;
                  triggerShockRef.current(node, delta);
                }}
                style={{ background: 'rgba(255,180,30,0.15)', border: '1px solid rgba(255,180,30,0.4)', color: 'rgba(255,180,30,0.9)', padding: '7px 16px', fontSize: 12, fontWeight: 700, borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
              >
                ⚡ Fire
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Layer buttons */}
      <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'center', zIndex: 10, padding: '0 12px' }}>
        {LAYER_BUTTONS.map((btn, i) => (
          <button
            key={btn.key}
            ref={el => { layerBtnsRef.current[btn.key] = el; }}
            onClick={() => layerClickRef.current?.(btn.key)}
            style={i === 0 ? BTN_ACTIVE : BTN_BASE}
          >
            {btn.label}
          </button>
        ))}
        <button
          onClick={openQuiz}
          style={{ ...BTN_BASE, borderColor: overlayMode === 'personal' ? 'rgba(251,191,36,0.45)' : undefined, color: overlayMode === 'personal' ? 'rgba(251,191,36,0.75)' : undefined }}
        >
          Quiz
        </button>
        {overlayMode === 'personal' && (
          <button
            onClick={() => setProfileOpen(true)}
            style={{ ...BTN_BASE, borderColor: 'rgba(139,92,246,0.45)', color: 'rgba(139,92,246,0.85)' }}
          >
            Profile Summary
          </button>
        )}
        <button
          onClick={() => {
            const next = !shockMode;
            setShockMode(next);
            if (!next) { setShockPanelVisible(false); setShockResult(null); shockResultRef.current = null; setMobileShockView(false); }
          }}
          style={{
            ...BTN_BASE,
            borderColor: shockMode ? 'rgba(255,180,30,0.45)' : undefined,
            color: shockMode ? 'rgba(255,180,30,0.85)' : undefined,
          }}
          title="Shock simulation mode — click any node to send a cost wave through the graph"
        >
          ⚡ {shockMode ? 'Shock ON' : 'Shock'}
        </button>
      </div>

      {/* Fullscreen — hidden when already on the standalone page */}
      {location.pathname !== '/viz/reality-layers' && (
        <button
          onClick={() => navigate('/viz/reality-layers')}
          style={{
            ...BTN_BASE,
            position: 'absolute',
            bottom: 20,
            right: 20,
            zIndex: 10,
            padding: '5px 8px',
            lineHeight: 0,
          }}
          title="Open fullscreen"
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 4.5V1h3.5M8.5 1H12v3.5M12 8.5V12H8.5M4.5 12H1V8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Quiz Modal */}
      {quizOpen && quizData && currentQ && (
        <div className="rl-quiz-overlay" onClick={e => { if (e.target === e.currentTarget) setQuizOpen(false); }}>
          <div className="rl-quiz-card">

            {/* Header */}
            <div className="rl-quiz-header">
              <span className="rl-quiz-domain">{currentDomain?.label}</span>
              <span className="rl-quiz-step">
                {domainIdx + 1} / {totalDomains}
              </span>
            </div>
            <div className="rl-quiz-progress-bar">
              <div className="rl-quiz-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>

            {/* Main question */}
            <div className="rl-quiz-q-text">{currentQ.text}</div>
            {currentQ.subtext && <div className="rl-quiz-q-sub">{currentQ.subtext}</div>}

            {/* Answer inputs */}
            {currentQ.type === 'binary' && (
              <div className="rl-quiz-binary">
                {currentQ.answers.map(ans => (
                  <button
                    key={ans.value}
                    className={`rl-quiz-binary-btn${selectedAnswer?.value === ans.value ? ' rl-quiz-selected' : ''}`}
                    onClick={() => {
                      setSelectedAnswer(ans);
                      if (ans.followup) {
                        setShowFollowup(true);
                      } else {
                        // no followup — auto-advance
                        advanceQuestion(ans, null);
                      }
                    }}
                  >
                    {ans.label}
                  </button>
                ))}
              </div>
            )}

            {currentQ.type === 'scale' && (
              <>
                <div className="rl-quiz-scale-row">
                  {currentQ.answers.map(ans => (
                    <button
                      key={ans.value}
                      className={`rl-quiz-scale-btn${selectedAnswer?.value === ans.value ? ' rl-quiz-selected' : ''}`}
                      onClick={() => {
                        setSelectedAnswer(ans);
                        // auto-advance on scale pick
                        advanceQuestion(ans, null);
                      }}
                    >
                      {ans.value}
                    </button>
                  ))}
                </div>
                {currentQ.scaleLabels && (
                  <div className="rl-quiz-scale-labels">
                    <span>{currentQ.scaleLabels.low}</span>
                    <span>{currentQ.scaleLabels.high}</span>
                  </div>
                )}
              </>
            )}

            {currentQ.type === 'multiselect' && (
              <div className="rl-quiz-multi">
                {currentQ.answers.map(ans => (
                  <button
                    key={ans.value}
                    className={`rl-quiz-multi-btn${selectedAnswer?.value === ans.value ? ' rl-quiz-selected' : ''}`}
                    onClick={() => setSelectedAnswer(ans)}
                  >
                    {ans.label}
                  </button>
                ))}
              </div>
            )}

            {/* Inline followup */}
            {showFollowup && followupQ && (
              <div className="rl-quiz-followup">
                <div className="rl-quiz-followup-label">Follow-up</div>
                <div className="rl-quiz-followup-text">{followupQ.text}</div>
                {followupQ.subtext && <div className="rl-quiz-followup-sub">{followupQ.subtext}</div>}

                {followupQ.type === 'scale' && (
                  <>
                    <div className="rl-quiz-scale-row">
                      {followupQ.answers.map(ans => (
                        <button
                          key={ans.value}
                          className={`rl-quiz-scale-btn${selectedFollowup?.value === ans.value ? ' rl-quiz-selected' : ''}`}
                          onClick={() => {
                            setSelectedFollowup(ans);
                            advanceQuestion(selectedAnswer, ans);
                          }}
                        >
                          {ans.value}
                        </button>
                      ))}
                    </div>
                    {followupQ.scaleLabels && (
                      <div className="rl-quiz-scale-labels">
                        <span>{followupQ.scaleLabels.low}</span>
                        <span>{followupQ.scaleLabels.high}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Nav: skip + next (only for multiselect or binary-with-followup-pending) */}
            {(currentQ.type === 'multiselect' || showFollowup) && (
              <div className="rl-quiz-nav">
                <button className="rl-quiz-skip" onClick={skipQuestion}>Skip</button>
                {currentQ.type === 'multiselect' && (
                  <button
                    className="rl-quiz-next"
                    disabled={!canAdvance}
                    onClick={() => canAdvance && advanceQuestion(selectedAnswer, null)}
                  >
                    {isLastStep ? 'Submit' : 'Next'}
                  </button>
                )}
              </div>
            )}

            {/* Skip-only row for binary/scale when no followup is showing */}
            {currentQ.type !== 'multiselect' && !showFollowup && (
              <div className="rl-quiz-nav">
                <button className="rl-quiz-skip" onClick={skipQuestion}>Skip</button>
                {isLastStep && canAdvance && (
                  <button className="rl-quiz-next" onClick={() => advanceQuestion(selectedAnswer, null)}>Submit</button>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* ─── Profile Summary Panel ───────────────────────────────────── */}
      {profileOpen && overlayMode === 'personal' && (() => {
        const nodes = allNodesRef.current;
        const overrides = quizOverridesRef.current;
        const LAYERS_META = layersMetaRef.current;

        // World budget (raw base costs, all nodes)
        const worldBudget = { attention: 0, energy: 0, money: 0 };
        nodes.forEach(n => {
          worldBudget.attention += n.cost?.attention || 0;
          worldBudget.energy += n.cost?.energy || 0;
          worldBudget.money += n.cost?.money || 0;
        });

        // Personal active budget (effective_cost, conscious threshold)
        const personalBudget = { attention: 0, energy: 0, money: 0 };
        nodes.forEach(n => {
          if (!n.overlay_active) return;
          const tot = (n.effective_cost?.attention || 0) + (n.effective_cost?.energy || 0) + (n.effective_cost?.money || 0);
          if (tot < 0.1) return;
          personalBudget.attention += n.effective_cost?.attention || 0;
          personalBudget.energy += n.effective_cost?.energy || 0;
          personalBudget.money += n.effective_cost?.money || 0;
        });

        // Active nodes ranked by personal load (weighted sum of effective_cost / personalBudget)
        const WEIGHTS = { attention: 0.4, energy: 0.35, money: 0.25 };
        const activeRanked = nodes
          .filter(n => n.overlay_active && overrides[n.id])
          .map(n => {
            const ec = n.effective_cost || {};
            const loadRatio = Math.round(
              (personalBudget.attention > 0 ? (ec.attention || 0) / personalBudget.attention : 0) * WEIGHTS.attention * 100 +
              (personalBudget.energy > 0 ? (ec.energy || 0) / personalBudget.energy : 0) * WEIGHTS.energy * 100 +
              (personalBudget.money > 0 ? (ec.money || 0) / personalBudget.money : 0) * WEIGHTS.money * 100
            );
            const baseCost = (n.cost?.attention || 0) + (n.cost?.energy || 0) + (n.cost?.money || 0);
            const effCost = (ec.attention || 0) + (ec.energy || 0) + (ec.money || 0);
            const delta = Math.round((effCost - baseCost) * 100);
            return { node: n, loadRatio, delta };
          })
          .sort((a, b) => b.loadRatio - a.loadRatio);

        // Inactive nodes (quiz set active: false)
        const inactiveNodes = nodes.filter(n => !n.overlay_active && overrides[n.id]?.active === false);

        // Budget delta vs world
        const budgetDelta = {
          attention: Math.round((personalBudget.attention - worldBudget.attention) * 10) / 10,
          energy: Math.round((personalBudget.energy - worldBudget.energy) * 10) / 10,
          money: Math.round((personalBudget.money - worldBudget.money) * 10) / 10,
        };

        const LAYER_COLORS = {
          physical: '#94a3b8', personal_physical: '#34d399', family: '#f472b6',
          spirituality: '#a78bfa', social: '#60a5fa', work: '#fbbf24',
          civic_government: '#f87171', entertainment: '#fb923c',
        };

        const res = [
          { key: 'attention', label: 'ATT', color: '#a78bfa' },
          { key: 'energy', label: 'NRG', color: '#34d399' },
          { key: 'money', label: 'MON', color: '#fbbf24' },
        ];

        return (
          <div className="rl-quiz-overlay" onClick={() => setProfileOpen(false)}>
            <div
              className="rl-quiz-card rl-profile-card"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 540 }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>Your Reality Profile</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 3 }}>Personal mode · {Object.keys(overrides).length} nodes configured</div>
                </div>
                <button onClick={() => setProfileOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
              </div>

              {/* Budget bars */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', marginBottom: 10 }}>Active resource budget</div>
                {res.map(({ key, label, color }) => {
                  const personal = personalBudget[key];
                  const world = worldBudget[key];
                  const maxVal = Math.max(personal, world, 0.1);
                  const pctP = Math.min(100, (personal / maxVal) * 100);
                  const pctW = Math.min(100, (world / maxVal) * 100);
                  const delta = budgetDelta[key];
                  const dColor = delta > 0 ? '#ff6b6b' : delta < 0 ? '#6bcb77' : 'rgba(255,255,255,0.3)';
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ fontSize: 8, fontWeight: 700, color, width: 26, flexShrink: 0 }}>{label}</span>
                      <div style={{ flex: 1, position: 'relative', height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pctW}%`, background: `${color}33`, borderRadius: 4 }} />
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pctP}%`, background: `${color}99`, borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', width: 32, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{personal.toFixed(1)}</span>
                      {delta !== 0 && <span style={{ fontSize: 9, color: dColor, width: 36, fontVariantNumeric: 'tabular-nums' }}>{delta > 0 ? '+' : ''}{delta}</span>}
                    </div>
                  );
                })}
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.18)', marginTop: 4 }}>dim bar = world baseline · bright bar = your profile</div>
              </div>

              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 0 18px' }} />

              {/* Top loaded nodes */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', marginBottom: 10 }}>
                  Top nodes by personal load
                </div>
                {activeRanked.slice(0, 8).map(({ node, loadRatio, delta }, i) => {
                  const layerColor = LAYER_COLORS[node.layer] || '#ffffff';
                  const loadColor = loadRatio >= 15 ? '#ff6b6b' : loadRatio >= 8 ? '#ffb347' : '#6bcb77';
                  return (
                    <div key={node.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', width: 14, flexShrink: 0 }}>{i + 1}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.label}</div>
                        <div style={{ fontSize: 9, color: layerColor, opacity: 0.7, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{LAYERS_META[node.layer]?.label || node.layer}</div>
                      </div>
                      {delta !== 0 && (
                        <span style={{ fontSize: 9, color: delta > 0 ? '#ff8888' : '#6bcb77', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                          {delta > 0 ? '+' : ''}{delta}%
                        </span>
                      )}
                      <span style={{ fontSize: 13, fontWeight: 700, color: loadColor, fontVariantNumeric: 'tabular-nums', width: 36, textAlign: 'right', flexShrink: 0 }}>
                        {loadRatio}%
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Absent nodes */}
              {inactiveNodes.length > 0 && (
                <>
                  <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 0 16px' }} />
                  <div>
                    <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', marginBottom: 8 }}>Absent from your life</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {inactiveNodes.map(n => (
                        <span key={n.id} style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, padding: '2px 7px', textDecoration: 'line-through', textDecorationColor: 'rgba(255,255,255,0.15)' }}>
                          {n.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Footer actions */}
              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="rl-quiz-skip" onClick={() => { setProfileOpen(false); openQuiz(); }}>Retake Quiz</button>
                <button className="rl-quiz-next" onClick={() => setProfileOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Shock controls panel ── */}
      {!isMobileLayout && shockMode && (
        <div className="rl-shock-controls">
          <div className="rl-shock-controls-header">
            <span className="rl-shock-controls-title">Configure shock</span>
            <span className="rl-shock-controls-hint">then click any node →</span>
          </div>

          <div className="rl-shock-type-row">
            {['additive', 'multiplicative', 'boolean'].map(t => (
              <button
                key={t}
                onClick={() => setShockType(t)}
                className={`rl-shock-type-btn${shockType === t ? ' active' : ''}`}
              >
                {t === 'additive' ? '+Δ add' : t === 'multiplicative' ? '×k scale' : '✗ remove'}
              </button>
            ))}
          </div>

          {shockType !== 'boolean' && (
            <>
              <div className="rl-shock-presets">
                <span className="rl-shock-presets-label">Presets</span>
                {[
                  { label: 'Job loss',      delta: { attention: 0.6, energy: 0.4, money: 0.7 } },
                  { label: 'Bereavement',   delta: { attention: 0.8, energy: 0.6, money: 0.1 } },
                  { label: 'New partner',   delta: { attention: -0.3, energy: -0.2, money: 0.1 } },
                  { label: 'Illness onset', delta: { attention: 0.5, energy: 0.7, money: 0.3 } },
                  { label: 'Recovery',      delta: { attention: -0.4, energy: -0.5, money: -0.1 } },
                ].map(({ label, delta }) => (
                  <button
                    key={label}
                    className="rl-shock-preset-btn"
                    onClick={() => {
                      setShockDelta(delta);
                      shockDeltaRef.current = delta;
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {[
                { key: 'attention', label: 'ATT', color: '#a78bfa' },
                { key: 'energy',    label: 'NRG', color: '#34d399' },
                { key: 'money',     label: 'MON', color: '#fbbf24' },
              ].map(({ key, label, color }) => (
                <div key={key} className="rl-shock-slider-row">
                  <span className="rl-shock-slider-label" style={{ color }}>{label}</span>
                  <input
                    type="range"
                    min="-1" max="1" step="0.05"
                    value={shockDelta[key]}
                    className="rl-shock-slider"
                    onChange={e => {
                      const v = parseFloat(e.target.value);
                      const next = { ...shockDelta, [key]: v };
                      setShockDelta(next);
                      shockDeltaRef.current = next;
                    }}
                  />
                  <span
                    className="rl-shock-slider-val"
                    style={{ color: shockDelta[key] > 0 ? '#ff8888' : shockDelta[key] < 0 ? '#6bcb77' : 'rgba(255,255,255,0.3)' }}
                  >
                    {shockDelta[key] > 0 ? '+' : ''}{Math.round(shockDelta[key] * 100)}%
                  </span>
                </div>
              ))}
            </>
          )}

          {shockType === 'boolean' && (
            <div className="rl-shock-bool-note">
              Click a node to remove it from the graph and watch its load redistribute to connected nodes.
            </div>
          )}

          {selectedShockNode && (
            <div className="rl-shock-last-trigger">
              Last: <strong>{selectedShockNode}</strong>
              <button
                className="rl-shock-replay-btn"
                onClick={() => {
                  const result = shockResultRef.current;
                  if (!result) return;
                  const node = allNodesRef.current.find(n => n.id === result.originId);
                  if (node) triggerShockRef.current?.(node, shockDeltaRef.current);
                }}
              >replay ↺</button>
            </div>
          )}
        </div>
      )}

      {/* ── Shock result panel ── */}
      {!isMobileLayout && shockPanelVisible && shockResult && (() => {
        const result = shockResult;
        const LAYER_COLORS = {
          physical: '#94a3b8', personal_physical: '#34d399', family: '#f472b6',
          spirituality: '#a78bfa', social: '#60a5fa', work: '#fbbf24',
          civic_government: '#f87171', entertainment: '#fb923c',
        };
        const totalCascadeNodes = result.cascade.length;
        const positiveNodes = result.cascade.filter(e => e.deltaAttention + e.deltaEnergy + e.deltaMoney > 0).length;
        const negativeNodes = totalCascadeNodes - positiveNodes;
        const maxMag = result.cascade[0]?.totalAbsDelta || 1;

        return (
          <div className="rl-shock-result-panel">
            <div className="rl-shock-result-header">
              <div>
                <div className="rl-shock-result-title">
                  ⚡ Cascade from <span style={{ color: LAYER_COLORS[result.originLayer] || '#fff' }}>{result.originLabel}</span>
                </div>
                <div className="rl-shock-result-meta">
                  {totalCascadeNodes} nodes affected · {positiveNodes} increased{negativeNodes > 0 ? ` · ${negativeNodes} relieved` : ''}
                </div>
              </div>
              <button onClick={() => { setShockPanelVisible(false); setShockResult(null); }} className="rl-shock-result-close">✕</button>
            </div>

            <div className="rl-shock-origin-delta">
              {[
                { key: 'attention', label: 'ATT', color: '#a78bfa' },
                { key: 'energy',    label: 'NRG', color: '#34d399' },
                { key: 'money',     label: 'MON', color: '#fbbf24' },
              ].map(({ key, label, color }) => {
                const v = result.delta[key];
                if (Math.abs(v) < 0.005) return null;
                return (
                  <span key={key} style={{ color, fontSize: 10, fontWeight: 700 }}>
                    {label} {v > 0 ? '+' : ''}{Math.round(v * 100)}%
                  </span>
                );
              })}
              <span className="rl-shock-origin-label">applied to origin</span>
            </div>

            <div className="rl-shock-result-divider" />

            <div className="rl-shock-cascade-list">
              {result.cascade.slice(0, 12).map((entry, i) => {
                const totalDelta = entry.deltaAttention + entry.deltaEnergy + entry.deltaMoney;
                const isPositive = totalDelta > 0;
                const barWidth   = Math.min(100, (entry.totalAbsDelta / maxMag) * 100);
                const layerColor = LAYER_COLORS[entry.layer] || '#ffffff';
                const depthDots  = '·'.repeat(entry.depth);

                return (
                  <div key={entry.nodeId} className="rl-shock-cascade-row">
                    <div className="rl-shock-cascade-rank">
                      <span className="rl-shock-cascade-depth">{depthDots}</span>
                      <span className="rl-shock-cascade-num">{i + 1}</span>
                    </div>
                    <div className="rl-shock-cascade-info">
                      <div className="rl-shock-cascade-name">{entry.label}</div>
                      <div className="rl-shock-cascade-layer" style={{ color: layerColor }}>{entry.layer.replace('_', ' ')}</div>
                      <div className="rl-shock-cascade-resources">
                        {[
                          { key: 'deltaAttention', label: 'ATT', color: '#a78bfa', val: entry.deltaAttention },
                          { key: 'deltaEnergy',    label: 'NRG', color: '#34d399', val: entry.deltaEnergy },
                          { key: 'deltaMoney',     label: 'MON', color: '#fbbf24', val: entry.deltaMoney },
                        ].filter(r => Math.abs(r.val) > 0.005).map(r => (
                          <span key={r.key} style={{ color: r.color, fontSize: 9 }}>
                            {r.label} {r.val > 0 ? '+' : ''}{Math.round(r.val * 100)}%
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="rl-shock-cascade-bar-col">
                      <div className="rl-shock-cascade-bar-track">
                        <div
                          className="rl-shock-cascade-bar-fill"
                          style={{
                            width: `${barWidth}%`,
                            background: isPositive
                              ? `rgba(255,${Math.round(80 + (1 - barWidth / 100) * 100)},80,0.7)`
                              : 'rgba(100,210,120,0.7)',
                          }}
                        />
                      </div>
                      <span className="rl-shock-cascade-total" style={{ color: isPositive ? '#ff8888' : '#6bcb77' }}>
                        {isPositive ? '+' : ''}{Math.round(totalDelta * 100)}%
                      </span>
                    </div>
                  </div>
                );
              })}
              {result.cascade.length > 12 && (
                <div className="rl-shock-cascade-more">+{result.cascade.length - 12} more nodes affected</div>
              )}
            </div>

            <div className="rl-shock-edge-legend">
              {['drain','amplify','conflict','support'].map(t => (
                <span key={t} className={`rl-conn-badge rl-conn-type-${t}`}>{t}</span>
              ))}
              <span className="rl-shock-edge-legend-note">edge types in cascade path</span>
            </div>
          </div>
        );
      })()}

      {/* Hover tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          background: 'rgba(4,6,12,0.92)',
          border: '1px solid rgba(255,255,255,0.13)',
          color: 'rgba(255,255,255,0.78)',
          padding: '10px 14px',
          fontSize: 11,
          fontWeight: 300,
          pointerEvents: 'none',
          display: 'none',
          zIndex: 20,
          maxWidth: 220,
          lineHeight: 1.6,
          borderRadius: 4,
          backdropFilter: 'blur(10px)',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      />
    </div>
  );
};

export default RealityLayersViz;